// React imports
import React, { useState, useRef, useEffect } from 'react';
import { useDrop } from 'react-dnd';

// Generic imports
import _ from 'lodash';
import { evaluate } from 'mathjs';
import dayjs from 'dayjs';
import { v4 as uuidv4 } from 'uuid';

// FontAwesomeIcon imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEdit, faTrash, faEllipsis, faBarcodeRead, faEye, faEyeSlash, faPlus, faShuffle, faFileCirclePlus, faCirclePlus, faCalendar, faSave, faExclamationCircle, faClone, faDollar, faDollarSign, faFileExport } from '@fortawesome/pro-light-svg-icons';
import { faCheck, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { faCircleXmark } from '@fortawesome/free-solid-svg-icons';

// Mui imports
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Menu from '@mui/material/Menu';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Drawer from '@mui/material/Drawer';
import Checkbox from '@mui/material/Checkbox';
import Table from '@mui/material/Table';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import { grey, blue, red, green } from '@mui/material/colors';
import Popover from '@mui/material/Popover';
import { useTheme } from '@mui/material/styles';
import pluralize from 'pluralize';

// Local imports
import OpportunityTitleEditModal from '../opportunities/OpportunityTitleEditModal'
import FieldSettingsDialog from '../opportunities/OpportunityOneOffEmailBuilder.fielddialog.js';
import OpportunityContractAddRowsModal from '../opportunities/OpportunityContractAddRowsModal'
import OpportunityContractEditLineModal from '../opportunities/OpportunityContractEditLineModal.js'
import OpportunityCargowiseCodesModal from '../opportunities/OpportunityCargowiseCodesModal.js';
import ProfitWidget from '../components/ProfitWidget.js';
import { getMultiplier } from '../../CalcWeight.js';
import { getProductInOffer, setProductForOffer } from '../utils/opportunity.js';
import currencyToText from '../../CurrencyToText';
import CurrencyFormulaTextField from '../components/CurrencyFormulaTextField.js';
import TitleableReorders from '../components/TitleableReorders.js';
import ContextMenu from '../components/ContextMenu.js';
import AddNewGroup from './AddNewGroup.js';
import { formulaToReact } from '../../utils/Formula.js';
import { ratesToText, getTotalRates, getFieldRatesForProduct, convertRateKgToLbsIfNeeded } from '../utils/rates.js';
import RatesDetails from '../../RatesDetails.js';
import { RateCard } from '../../RateCard.js';
import { FieldTypes } from '../../FieldTypes.js';
import CurrencyDialog from '../../CurrencyDialog.js';
import { calcFieldValue } from '../../utils/opportunity.js';

// Monada imports
import SupplierLogos from '@monada-ai/monada-shipping-companies-logos';

function DensityRatioInline({ value, onChange }) {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [editValue, setEditValue] = React.useState(value);
    return (
        <>
            <span
                style={{ display: 'block', fontSize: '10px', color: '#64748b', cursor: 'pointer', borderBottom: '1px dashed #94a3b8', width: 'fit-content' }}
                onClick={(e) => { e.stopPropagation(); setEditValue(value); setAnchorEl(e.currentTarget); }}
            >
                Density: {value}
            </span>
            <Popover
                open={!!anchorEl}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                onClick={(e) => e.stopPropagation()}
                sx={{ zIndex: 1000001 }}
            >
                <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TextField
                        size="small"
                        label="Density Ratio"
                        type="number"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        sx={{ width: '120px' }}
                        autoFocus
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                onChange(parseFloat(editValue) || 1000);
                                setAnchorEl(null);
                            }
                        }}
                    />
                    <Button
                        size="small"
                        variant="contained"
                        onClick={() => {
                            onChange(parseFloat(editValue) || 1000);
                            setAnchorEl(null);
                        }}
                    >
                        Save
                    </Button>
                </Box>
            </Popover>
        </>
    );
}

// Each field type reads its amount from a different `values` key: per-unit / per-unit-type read
// per-product keys, `flat` reads `values.flat`, `custom` reads `values.custom`, `string` reads
// `values.string`. Changing a field's type therefore used to orphan the existing value/formula
// under the old key (e.g. per-unit-type -> flat left the formula under the product id, invisible
// to the flat cell — so the formula looked deleted and couldn't be re-applied across records).
// Carry the existing entry into the new type's key so the formula survives a type change. Old keys
// are left in place (only the active type's key is ever read), so switching back is lossless too.
function migrateFieldValuesForType(field, newType, products) {
    const values = field.values || {};
    const hasData = (v) => v && (v.formula != null || v.value != null);
    const rep = _.cloneDeep(_.find(values, hasData) || _.values(values)[0] || {});
    if (newType === 'flat') {
        if (!hasData(values.flat)) values.flat = rep;
    } else if (newType === 'custom') {
        if (!hasData(values.custom)) values.custom = rep;
    } else if (newType === 'string') {
        if (!values.string) values.string = { value: rep.value != null ? rep.value : '' };
    } else {
        // per-unit / per-unit-type: one key per product
        (products || []).forEach(p => { if (!hasData(values[p.id])) values[p.id] = _.cloneDeep(rep); });
    }
    field.values = values;
}

function ContractBuilder(props) {
    const { org, mode, routes, products, setRoutes, seaPorts, transportationMethod, locked, unlocode,
        offers, setOffers, dirty, onAdminDelete, onDeleteOffer, onSave, saving, savingSuccess, savingError, savingErrorMessages = {}, onCreateTemplate, 
        buyingRates, setBuyingRates, suppliers, accesstoken, minimizedSearch, size, me, exchangeRates } = props;
    const [ offerGroups, setOfferGroups ] = useState(null);
    const [ modal, setModal ] = useState({ open: false });
    const [ addRows, setAddRows ] = useState({ open: false });
    const [ editLine, setEditLine ] = useState(null); // { offerId, line: { routes, product } }
    const [ editable, setEditable ] = useState(null);
    const [ selected, setSelected ] = useState(null);
    const [ highlighted, setHighlighted ] = useState(null);
    const [ commitOnMovement, setCommitOnMovement ] = useState(null);
    const [ sortModal, setSortModal ] = useState({ open: false });
    const [ cargowiseCodes, setCargowiseCodes ] = useState(null);
    const [ ratesMoreInfo, setRatesMoreInfo ] = useState({ open: false });
    const [ checkboxes, setCheckboxes ] = useState([]);
    const [ adminDeleting, setAdminDeleting ] = useState(null);
    const [ currencySelector, setCurrencySelector ] = useState({ open: false });

    const { fontSize, cellWidth } = size === 'small' ? { fontSize: '11px', cellWidth: 100 } : { fontSize: '13px', cellWidth: 150 };

    const tableContentRef = useRef();
    tableContentRef.current = [];

    const editableRef = useRef();
    editableRef.current = editable;
    const setEditableRef = useRef();
    setEditableRef.current = setEditable;
    const selectedRef = useRef();
    selectedRef.current = selected;
    const setSelectedRef = useRef();
    setSelectedRef.current = setSelected;
    const commitOnMovementRef = useRef();
    commitOnMovementRef.current = commitOnMovement;
    const setCommitOnMovementRef = useRef();
    setCommitOnMovementRef.current = setCommitOnMovement;
    const modeRef = useRef();
    modeRef.current = mode;
    const callbacksRef = useRef();

    const _setSelected = (e) => {
        if (mode !== 'rates' && mode !== 'tariff' && mode !== 'opportunities') return;
        if (e !== selected) {
            setEditable(null);
        }

        setSelected(e);
        if (_.last(highlighted) !== e) {
            setHighlighted([e]);
        }
    }

    useEffect(() => {
        const groups = _.groupBy(offers, o => {
            if (mode === 'opportunities') {
                return `${o.title || ''}:${o.description || ''}:${o.validFrom}:${o.validUntil}:` + o.sections.map(s =>
                    `${s.title}:[` + s.offers[0].fields.map(f => `${f.title}|${f.type}`).join(',') + ']').join('|');
            } else {
                return `${o.title || ''}:${o.description || ''}:` + o.sections.map(s =>
                    `${s.title}:[` + s.offers[0].fields.map(f => `${f.title}|${f.type}`).join(',') + ']').join('|');
            }
        });
        setOfferGroups(og => {
            const newGroups = _.values(groups).map(group => ({ 
                id: `group-${group[0].id}`,
                base: {
                    title: group[0].title,
                    description: group[0].description,
                    sections: _.cloneDeep(group[0].sections),
                    validFrom: group[0].validFrom,
                    validUntil: group[0].validUntil,
                }, 
                offers: group.map(rate => rate.id) 
            }));
            return newGroups;
        });
    }, [offers]);

    useEffect(() => {
        const onKeyDown = (event) => {
            const currentMode = modeRef.current;
            if (currentMode !== 'rates' && currentMode !== 'tariff' && currentMode !== 'opportunities') return;

            const contractBuilder = document.getElementById('contractbuilder');
            const activeElement = document.activeElement;

            if (contractBuilder?.contains(activeElement) || activeElement === document.body) {
                // Tab key - navigate between cells
                if (event.keyCode === 9 && selectedRef.current) {
                    event.preventDefault();
                    if (event.shiftKey) {
                        callbacksRef.current.onPrevField(event);
                    } else {
                        callbacksRef.current.onNextField(event);
                    }
                }

                // Escape key - exit edit mode
                if (event.keyCode === 27 && editableRef.current) {
                    setEditableRef.current(null);
                    event.preventDefault();
                }

                if ((event.keyCode === 37) && selectedRef.current && (!editableRef.current || commitOnMovementRef.current)) callbacksRef.current.onPrevField(event);
                if ((event.keyCode === 38) && selectedRef.current && (!editableRef.current || commitOnMovementRef.current)) callbacksRef.current.onPrevRoute(event);
                if ((event.keyCode === 39) && selectedRef.current && (!editableRef.current || commitOnMovementRef.current)) callbacksRef.current.onNextField(event);
                if ((event.keyCode === 40) && selectedRef.current && (!editableRef.current || commitOnMovementRef.current)) callbacksRef.current.onNextRoute(event);
            }

            const activeTextarea = document.activeElement;
            if (activeTextarea === document.body) {
                if ((event.keyCode === 13) && (!editableRef.current) && selectedRef.current) {
                    setCommitOnMovementRef.current(false);
                    setEditableRef.current(selectedRef.current);
                }

                if (!event.ctrlKey && !event.metaKey && !event.altKey) {
                    if ((event.keyCode >= 48) && (event.keyCode <= 57) && (!editableRef.current) && selectedRef.current) { // Numbers
                        setCommitOnMovementRef.current(true);
                        setEditableRef.current(selectedRef.current);
                    }
                    if ((event.keyCode >= 96) && (event.keyCode <= 105) && (!editableRef.current) && selectedRef.current) { // Keypad
                        setCommitOnMovementRef.current(true);
                        setEditableRef.current(selectedRef.current);
                    }
                    if ((event.keyCode >= 65) && (event.keyCode <= 90) && (!editableRef.current) && selectedRef.current) { // Letters
                        setCommitOnMovementRef.current(true);
                        setEditableRef.current(selectedRef.current);
                    }
                }
            }
        }

        window.addEventListener('keydown', onKeyDown);

        return () => {
            window.removeEventListener('keydown', onKeyDown);
        }
    }, []);

    function onPrevField(event) {
        const [ currentOfferId, currentFieldId ] = selected.split(':');
        const currentOffer = offers.find(o => o.id === currentOfferId);

        if (currentOffer) {
            const fields = mode === 'opportunities' ? 
                ['transitTime', 'transshipment', ..._.flatten(currentOffer.sections.map(s => s.offers[0].fields.map(f => f.id)))] :
                ['validFrom', 'validUntil', 'type', 'transitTime', 'transshipment', ..._.flatten(currentOffer.sections.map(s => s.offers[0].fields.map(f => f.id)))];
            const fieldIndex = fields.findIndex(f => f === currentFieldId);

            if (fieldIndex === 0) return null;
            const nextField = fields[fieldIndex - 1];

            setEditable(null);
            setSelected(`${currentOffer.id}:${nextField}`);
            if (event.shiftKey) {
                setHighlighted(h => [...h, `${currentOffer.id}:${nextField}`]);
            } else {
                setHighlighted([`${currentOffer.id}:${nextField}`]);
            }

            const groupIndex = offerGroups.findIndex(g => g.offers.includes(currentOffer.id));
            setTimeout(() => {
                tableContentRef.current[groupIndex].scrollLeft -= cellWidth;
            }, 10);
        } else if (currentFieldId) {
            const base = offerGroups.map(g => g.base).find(b => b.sections.map(s => s.id).includes(currentOfferId));
            const groupIndex = offerGroups.findIndex(g => g.base === base);
            const list = _.flatten(base.sections.map(s => s.offers[0].fields.map(f => `${s.id}:${f.id}`)));
            const index = list.indexOf(selected);
            if (index < 1) return null
            setEditable(null);
            setSelected(list[index - 1]);
            if (event.shiftKey) {
                setHighlighted(h => [...h, list[index - 1]]);
            } else {
                setHighlighted([list[index - 1]]);
            }

            setTimeout(() => {
                tableContentRef.current[groupIndex].scrollLeft -= cellWidth;
            }, 10);
        } else {
            const base = offerGroups.map(g => g.base).find(b => b.sections.map(s => s.id).includes(currentOfferId));
            const groupIndex = offerGroups.findIndex(g => g.base === base);
            const list = base.sections.map(s => s.id);
            const index = list.indexOf(selected);
            if (index < 1) return null
            setEditable(null);
            setSelected(list[index - 1]);
            if (event.shiftKey) {
                setHighlighted(h => [...h, list[index - 1]]);
            } else {
                setHighlighted([list[index - 1]]);
            }

            setTimeout(() => {
                tableContentRef.current[groupIndex].scrollLeft -= cellWidth * base.sections.find(s => s.id === list[index]).offers[0].fields.length;
            }, 10);
        }

        event.stopPropagation();
        event.preventDefault();
    }

    function onPrevRoute(event) {
        const [ currentOfferId, currentFieldId ] = selected.split(':');
        const currentOffer = offers.find(o => o.id === currentOfferId);
        const currentGroup = offerGroups.find(g => g.offers.includes(currentOfferId));
        const currentOfferIndexInGroup = currentGroup.offers.findIndex(o => o === currentOfferId);

        if (currentOfferIndexInGroup === 0) return null;
        const nextOffer = offers.find(o => o.id === currentGroup.offers[currentOfferIndexInGroup - 1]);;

        const sectionIndex = currentOffer.sections.findIndex(s => s.offers[0].fields.find(f => f.id === currentFieldId));
        const fieldIndex = sectionIndex > -1 ? currentOffer.sections[sectionIndex].offers[0].fields.findIndex(f => f.id === currentFieldId) : -1;

        setEditable(null);
        if (sectionIndex > -1) {
            const nextField = nextOffer.sections[sectionIndex].offers[0].fields[fieldIndex].id;
            setSelected(`${nextOffer.id}:${nextField}`);
            if (event.shiftKey) {
                setHighlighted(h => [...h, `${nextOffer.id}:${nextField}`]);
            } else {
                setHighlighted([`${nextOffer.id}:${nextField}`]);
            }
        } else {
            setSelected(`${nextOffer.id}:${currentFieldId}`); // valids, transit time, etc...
            if (event.shiftKey) {
                setHighlighted(h => [...h, `${nextOffer.id}:${currentFieldId}`]);
            } else {
                setHighlighted([`${nextOffer.id}:${currentFieldId}`]);
            }
        }
        event.stopPropagation();
        event.preventDefault();
    }

    // Adding a field or a section moves the selection on afterwards and calls this
    // with no event, so shiftKey has to be optional — reading it off an undefined
    // event crashed the whole builder.
    function onNextField(event) {
        const [ currentOfferId, currentFieldId ] = selected.split(':');
        let currentOffer = offers.find(o => o.id === currentOfferId);

        if (currentOffer) {
            const fields = mode === 'opportunities' ? 
                ['transitTime', 'transshipment', ..._.flatten(currentOffer.sections.map(s => s.offers[0].fields.map(f => f.id)))] :
                ['validFrom', 'validUntil', 'type', 'transitTime', 'transshipment', ..._.flatten(currentOffer.sections.map(s => s.offers[0].fields.map(f => f.id)))];
            const fieldIndex = fields.findIndex(f => f === currentFieldId);
            if (fieldIndex === fields.length - 1) return null;
            const nextField = fields[fieldIndex + 1];

            setEditable(null);
            setSelected(`${currentOffer.id}:${nextField}`);
            if (event?.shiftKey) {
                setHighlighted(h => [...h, `${currentOffer.id}:${nextField}`]);
            } else {
                setHighlighted([`${currentOffer.id}:${nextField}`]);
            }

            const groupIndex = offerGroups.findIndex(g => g.offers.includes(currentOffer.id));
            setTimeout(() => {
                tableContentRef.current[groupIndex].scrollLeft += cellWidth;
            }, 10);
        } else if (currentFieldId) {
            const base = offerGroups.map(g => g.base).find(b => b.sections.map(s => s.id).includes(currentOfferId));
            const groupIndex = offerGroups.findIndex(g => g.base === base);
            const list = _.flatten(base.sections.map(s => s.offers[0].fields.map(f => `${s.id}:${f.id}`)));
            const index = list.indexOf(selected);
            if ((index === -1) || (index === list.length - 1)) return null
            setEditable(null);
            setSelected(list[index + 1]);
            if (event?.shiftKey) {
                setHighlighted(h => [...h, list[index + 1]]);
            } else {
                setHighlighted([list[index + 1]]);
            }

            setTimeout(() => {
                tableContentRef.current[groupIndex].scrollLeft += cellWidth;
            }, 10);
        } else {
            const base = offerGroups.map(g => g.base).find(b => b.sections.map(s => s.id).includes(currentOfferId));
            const groupIndex = offerGroups.findIndex(g => g.base === base);
            const list = base.sections.map(s => s.id);
            const index = list.indexOf(selected);
            if ((index === -1) || (index === list.length - 1)) return null
            setEditable(null);
            setSelected(list[index + 1]);
            if (event?.shiftKey) {
                setHighlighted(h => [...h, list[index + 1]]);
            } else {
                setHighlighted([list[index + 1]]);
            }

            setTimeout(() => {
                tableContentRef.current[groupIndex].scrollLeft += cellWidth * base.sections.find(s => s.id === list[index]).offers[0].fields.length;
            }, 10);
        }

        if (event) {
            event.stopPropagation();
            event.preventDefault();
        }
    }

    function onNextRoute(event) {
        const [ currentOfferId, currentFieldId ] = selected.split(':');
        const currentOffer = offers.find(o => o.id === currentOfferId);
        const currentGroup = offerGroups.find(g => g.offers.includes(currentOfferId));
        const currentOfferIndexInGroup = currentGroup.offers.findIndex(o => o === currentOfferId);

        if (currentOfferIndexInGroup === currentGroup.offers.length - 1) return null;
        const nextOffer = offers.find(o => o.id === currentGroup.offers[currentOfferIndexInGroup + 1]);;

        const sectionIndex = currentOffer.sections.findIndex(s => s.offers[0].fields.find(f => f.id === currentFieldId));
        const fieldIndex = sectionIndex > -1 ? currentOffer.sections[sectionIndex].offers[0].fields.findIndex(f => f.id === currentFieldId) : -1;

        setEditable(null);
        if (sectionIndex > -1 ) {
            const selectedField = nextOffer.sections[sectionIndex].offers[0].fields[fieldIndex].id;
            setSelected(`${nextOffer.id}:${selectedField}`);
            if (event.shiftKey) {
                setHighlighted(h => [...h, `${nextOffer.id}:${selectedField}`]);
            } else {
                setHighlighted([`${nextOffer.id}:${selectedField}`]);
            }
        } else {
            setSelected(`${nextOffer.id}:${currentFieldId}`); // valids, transit time, etc...
            if (event.shiftKey) {
                setHighlighted(h => [...h, `${nextOffer.id}:${currentFieldId}`]);
            } else {
                setHighlighted([`${nextOffer.id}:${currentFieldId}`]);
            }
        }
        event.stopPropagation();
        event.preventDefault();
    }

    callbacksRef.current = { onPrevField, onPrevRoute, onNextField, onNextRoute };

    function onNewSection({ groupIndex, sectionIndex }) {
        const newOfferGroups = _.clone(offerGroups);
        const group = newOfferGroups[groupIndex];
        group.base.sections.splice(sectionIndex + 1, 0, { id: uuidv4(), title: 'New Section', offers: [{ id:uuidv4(), fields: [{ id: uuidv4(), type: 'per-unit-type', title: 'New Field', values: {} }] }]});
        setOfferGroups(newOfferGroups);

        const newOffers = _.clone(offers).map(_offer => {
            if (!group.offers.includes(_offer.id)) return _offer;
            const product = getProductInOffer({ offer: _offer, products }) || products[group.offers.indexOf(_offer.id) % products.length];
            const defaultCurrency = _.flatten(_offer.sections.map(section => section.offers[0].fields.map(f => _.values(f.values)[0].currency)))[0] || org.defaults?.currency;
            _offer.sections.splice(sectionIndex + 1, 0, {
                id: uuidv4(),
                title: 'New Section',
                offers: [{ 
                    id:uuidv4(),
                    fields: [{ 
                        id: uuidv4(), 
                        type: 'per-unit-type', 
                        title: 'New Field', 
                        values: { 
                            [product.id] : { 
                                currency: defaultCurrency,
                                value: 0 
                            },
                            string: {
                                value: ''
                            },
                            date: {
                                value: ''
                            }
                        }
                    }]
                }]
            });
            return _offer;
        });
        setOffers(newOffers);

        setTimeout(() => {
            if (tableContentRef.current[groupIndex]) {
                tableContentRef.current[groupIndex].scrollLeft += 300;
                callbacksRef.current.onNextField();
            }
        }, 10);
    }

    function onDeleteSection({ groupIndex, sectionIndex }) {
        const newOfferGroups = _.clone(offerGroups);
        const group = newOfferGroups[groupIndex];
        group.base.sections.splice(sectionIndex, 1);
        setOfferGroups(newOfferGroups);

        const newOffers = _.clone(offers).map(_offer => {
            if (!group.offers.includes(_offer.id)) return _offer;
            _offer.sections.splice(sectionIndex, 1);
            return _offer;
        });
        setOffers(newOffers);
    }

    function onNewField({ groupIndex, sectionIndex, fieldIndex }) {
        const newOfferGroups = _.clone(offerGroups);
        const group = newOfferGroups[groupIndex];
        group.base.sections[sectionIndex].offers[0].fields.splice(fieldIndex + 1, 0, { id: uuidv4(), type: 'per-unit-type', title: 'New Field', values: {} });
        setOfferGroups(newOfferGroups);

        const newOffers = _.clone(offers).map(_offer => {
            if (!group.offers.includes(_offer.id)) return _offer;
            const product = getProductInOffer({ offer: _offer, products }) || products[group.offers.indexOf(_offer.id) % products.length];
            const defaultCurrency = _.flatten(_offer.sections.map(section => section.offers[0].fields.map(f => _.values(f.values)[0].currency)))[0] || org.defaults?.currency;
            _offer.sections[sectionIndex].offers[0].fields.splice(fieldIndex + 1, 0, { 
                id: uuidv4(), 
                type: 'per-unit-type', 
                title: 'New Field', 
                values: { 
                    [product.id] : { 
                        currency: defaultCurrency, 
                        value: 0 
                    },
                    string: {
                        value: ''
                    },
                    date: {
                        value: ''
                    }
                } 
            });
            return _offer;
        });
        setOffers(newOffers);

        setTimeout(() => {
            if (tableContentRef.current[groupIndex]) {
                tableContentRef.current[groupIndex].scrollLeft += 300;
                callbacksRef.current.onNextField();
            }
        }, 10);
    }

    function onDeleteField({ groupIndex, sectionIndex, fieldIndex }) {
        const newOfferGroups = _.clone(offerGroups);
        const group = newOfferGroups[groupIndex];
        group.base.sections[sectionIndex].offers[0].fields.splice(fieldIndex, 1);
        setOfferGroups(newOfferGroups);

        const newOffers = _.clone(offers).map(_offer => {
            if (!group.offers.includes(_offer.id)) return _offer;
            _offer.sections[sectionIndex].offers[0].fields.splice(fieldIndex, 1);
            return _offer;
        });
        setOffers(newOffers);
    }

    function onChangeCurrency({ groupIndex, sectionIndex, fieldIndex }) {
        setCurrencySelector({ open: true, onDone: ({ currency }) => {
            setFieldProperty({ groupIndex, sectionIndex, fieldIndex, property: 'currency', value: currency });
        }});
    }

    function onDeleteGroup({ groupIndex }) {
        const group = offerGroups[groupIndex];

        const newOfferGroups = _.clone(offerGroups);
        newOfferGroups.splice(groupIndex, 1);
        setOfferGroups(newOfferGroups);

        setOffers(offers.filter(_offer => !group.offers.includes(_offer.id)));
    }

    function onAddRoutes({ routes, selectedProducts, groupIndex, offerId }) {
        const base = offerGroups[groupIndex].base;
        const newOffers = [];

        routes.forEach(route => {
            selectedProducts.forEach(product => {
                const newOffer = offerId ? 
                    setProductForOffer({ offer: { sections: offers.find(o => o.id === offerId).sections }, product, organization: org }) :
                    setProductForOffer({ offer: { sections: base.sections }, product, organization: org });
                newOffer.id = uuidv4();
                newOffer.sections.forEach(section => {
                    section.id = uuidv4();
                    section.offers.forEach(offer => offer.fields.forEach(field => field.id = uuidv4()))
                });
                newOffer.routes = [route];
                newOffer.title = base.title;
                newOffer.description = base.description;
                newOffer.validFrom = base.validFrom;
                newOffer.validUntil = base.validUntil;
                // setProductForOffer only stamps the product id into per-unit/per-unit-type field
                // values. When every charge field is `flat` (e.g. a per-shipment land rate), no
                // field ends up referencing the product, so getProductInOffer can't resolve it and
                // callers fall back to an index into the full products list — showing the wrong
                // product (e.g. always "Dry"). Stamp it directly, same as convertRateToOffer does
                // for saved rates. Skip for opportunities, whose offers are schema-validated
                // without a `product` field.
                if (mode !== 'opportunities') {
                    newOffer.product = product;
                }
                newOffers.push(newOffer);
            })
        });

        setOffers(o => {
            if (!offerId) {
                return [...o, ...newOffers];
            } else {
                const pos = offers.findIndex(o => o.id === offerId);
                return [ ..._.slice(o, 0, pos + 1), ...newOffers, ..._.slice(o, pos + 1)];
            }
        })

        setOfferGroups(o => {
            const o2 = _.cloneDeep(o);
            if (!offerId) {
                o2[groupIndex].offers = [...o2[groupIndex].offers, ...newOffers.map(o => o.id)];
            } else {
                const pos = o2[groupIndex].offers.findIndex(o => o === offerId);
                o2[groupIndex].offers = [ ..._.slice(o2[groupIndex].offers, 0, pos + 1), ...newOffers.map(o => o.id), ..._.slice(o2[groupIndex].offers, pos + 1)];
            }
            return o2;
        });
    }

    function _onDeleteOffer({ offerId }) {
        if (mode === 'tariff' || mode === 'rates-import') {
            onDeleteOffer({ offerId });
            return;
        }
        setOfferGroups(offerGroups.map(og => {
            return ({
                ...og,
                offers: og.offers.filter(oId => oId !== offerId)
            })
        }));
        setOffers(offers.filter(_offer => _offer.id !== offerId));
    }

    function onAddRate({ groupIndex }) {
        setAddRows({ open: true, groupIndex });
    }

    function onAddBelow({ groupIndex, offerId }) {
        setAddRows({ open: true, groupIndex, offerId });
    }

    function onExportGroup({ groupIndex }) {
        const group = offerGroups[groupIndex];
        const _offers = offers.filter(o => group.offers.includes(o.id));
        const exportData = {
            group,
            offers: _offers,
        }
        const data = [
            ['type', 'source', 'destination', 'transportationMethod', 'form', 'supplier', 'supplierEmail', 'validFrom', 'validUntil', 'transitTime', 'product', 'dangerous', 'transshipment']
        ];

        _offers.forEach(o => {
            const product = getProductInOffer({ offer: o, products });
            [product, 'flat'].map(p => {
                const productId = p.id;
                const offerFields = o.sections.map(s => s.offers.map(so => so.fields.filter(f => {
                    if (p === 'flat' && (f.type !== 'flat' && f.type !== 'string')) return false;
                    if (p !== 'flat' && (f.type === 'flat' || f.type === 'string')) return false;
                    return true;
                }))).flat().flat();

                const form = _.find(org.forms, (f, k) => {
                    if (f.deleted) return false;
                    if (f.transportationMethods && !f.transportationMethods.includes(transportationMethod)) return false;
                    if (f.type !== 'rate') return false;
                    const formFields = f.sections.map(s => s.fields).flat();
                    return offerFields.every(ff => formFields.find(of => of.title === ff.title));
                });

                const productType = (() => {
                    if (p === 'flat') return 'route';
                    if ((p.type === 'By Volume or Weight (sea)') && (product.volume)) return '1 cbm';
                    if ((p.type === 'By Volume or Weight (sea)') && (product.weight)) return '1 ton';
                    return p.type;
                })();

                const newRow = [
                    o.type, o.routes[0]?.source?.id || '', o.routes[0]?.destination?.id || '', transportationMethod, form?.title || '', o.supplier.organization, o.supplier.email || '', o.validFrom || '', o.validUntil || '', o.routes[0].transitTime || '', productType, product.dangerous, o.routes[0].transshipment || ''
                ];
                data[0].forEach((h, i) => (i > newRow.length - 1) ? newRow.push('') : '');

                offerFields.forEach(f => {
                    let fieldIndex = data[0].indexOf(f.title);
                    if (fieldIndex === -1) {
                        data[0].push(f.title);
                        newRow.push('');
                        fieldIndex = data[0].length - 1;
                    }
                    const pId = f.type === 'flat' ? 'flat' : f.type === 'string' ? 'string' : productId;
                    newRow[fieldIndex] = (f.values[pId]?.value || '') + '|' + (f.values[pId]?.currency || org.defaults?.currency || 'USD');
                })
                data.push(newRow);
            });
        });
        const longestRow = data.reduce((max, row) => Math.max(max, row.length), 0);
        data.forEach(row => {
            while (row.length < longestRow) {
                row.push('');
            }
        });

        // Create CSV content
        const csvContent = data.map(row => row.join(',')).join('\n');
        
        // Create blob and download link
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = 'export.csv';
        
        // Trigger download
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    function onDuplicate({ groupIndex, offerId }) {
        const offer = offers.find(o => o.id === offerId);
        const product = getProductInOffer({ offer, products });
        onAddRoutes({ routes: offer.routes, selectedProducts: [ product ], groupIndex, offerId });
    }

    // Change one line's route or product in place. The prices move onto the new product as they
    // are (setProductForOffer keeps each value, formula and currency), and the line keeps its id.
    function onEditLine({ offerId }) {
        const offer = offers.find(o => o.id === offerId);
        if (!offer) return;
        // Whether any charge on the line is priced from linked supplier costs: those are quoted for
        // one container type, so the edit window warns before the product type changes.
        const hasCosts = (offer.sections || []).some(section => (section.offers || []).some(so => (so.fields || []).some(f => (f.buyingRates || []).length > 0)));
        setEditLine({ offerId, line: { routes: offer.routes, product: getProductInOffer({ offer, products }), hasCosts } });
    }

    function onApplyEditLine({ source, destination, product }) {
        const { offerId } = editLine;
        setOffers(o => o.map(offer => {
            if (offer.id !== offerId) return offer;
            const current = getProductInOffer({ offer, products });
            let repriced = offer;
            if (current && current.id !== product.id) {
                // Move everything this line keys by its product onto the new one: every field's
                // value for the old product (prices, formulas, and the text fields the product is
                // read back from) and every supplier cost linked to it. A price that is an uplift
                // on a cost keeps its cost; nothing else in the line changes.
                const sections = _.cloneDeep(offer.sections);
                sections.forEach(section => (section.offers || []).forEach(sectionOffer => (sectionOffer.fields || []).forEach(field => {
                    if (field.values && current.id in field.values) {
                        field.values[product.id] = field.values[current.id];
                        delete field.values[current.id];
                    }
                    field.buyingRates = (field.buyingRates || []).map(br => (br.opportunityProductId === current.id ? { ...br, opportunityProductId: product.id } : br));
                })));
                repriced = { ...offer, sections };
            } else if (!current) {
                repriced = { ...offer, sections: setProductForOffer({ offer: { sections: offer.sections }, product, organization: org }).sections };
            }
            return {
                ...repriced,
                routes: [{ ...(offer.routes?.[0] || {}), source, destination }, ...(offer.routes || []).slice(1)],
                ...(mode !== 'opportunities' ? { product } : {}),
            };
        }));
        setEditLine(null);
    }

    function onHideOffer({ offerId }) {
        const o = _.cloneDeep(offers);
        const offer = o.find(oo => oo.id === offerId);
        offer.hidden = !offer.hidden;
        setOffers(o);
    }

    function setField({ path, value }) {
        const o = _.cloneDeep(offers);
        // highlighted starts as null, not an empty array, so editing a cell before anything
        // has been highlighted - double clicking straight into one - reached .length on null
        // and threw. Nothing highlighted means nothing to apply across.
        if ((highlighted || []).length > 1) {
            highlighted.forEach(h => {
                const [offerId, fieldId] = h.split(':');
                const offerIndex = o.findIndex(o => o.id === offerId);
                if (fieldId === 'validFrom' || fieldId === 'validUntil' || fieldId === 'type' || fieldId === 'transshipment' || fieldId === 'transitTime') {
                    o[offerIndex][fieldId] = value;
                } else {
                    const field = o[offerIndex].sections.map(s => s.offers.map(o => o.fields)).flat().flat().find(f => f.id === fieldId);
                    if (field?.type === 'string') {
                        field.values = field.values || {};
                        field.values.string = { ...field.values.string, value };
                    } else if (field) {
                        const srcOffer = _.get(o, path.split('.')[0], null);
                        const destOffer = o[offerIndex];
                        if (srcOffer === destOffer) return;
                        const srcProduct = getProductInOffer({ offer: srcOffer, products });
                        const destProduct = getProductInOffer({ offer: destOffer, products });

                        // Fix the 1ton issue
                        if (srcProduct.type === 'By Volume or Weight (sea)' || destProduct.type === 'By Volume or Weight (sea)') {
                            if (srcProduct.weight === 1000 && destProduct.weight === 1000) {
                                field.values = _.mapValues(field.values, v => ({ ...v, ...value }));
                                field.values.flat = { ...(field.values.flat || {}), ...value };
                            } else if (destProduct.weight === 1000) {
                                field.values = _.mapValues(field.values, v => ({ ...v, ...value, value: value.value / 1000 }));
                                field.values.flat = { ...(field.values.flat || {}), ...value, value: value.value / 1000 };
                            } else if (srcProduct.weight === 1000) {
                                field.values = _.mapValues(field.values, v => ({ ...v, ...value, value: value.value * 1000 }));
                                field.values.flat = { ...(field.values.flat || {}), ...value, value: value.value * 1000 };
                            }
                        } else {
                            field.values = _.mapValues(field.values, v => ({ ...v, ...value }));
                            field.values.flat = { ...(field.values.flat || {}), ...value };
                        }

                        if (value.formula) {
                            console.log('@@@@@@@@@@@@@@@@@@@@@@@@', value.formula);
                        }
                    }
                }
            })
        }
        _.set(o, path, value);
        setOffers(o);
    }

    // A rate row prices exactly one product — setProductForOffer writes a single key into
    // `values` — so switching a charge's calculation method must stamp only that product.
    // Stamping every product in the mode (40 of them for land) left the field with 40 keys,
    // and getProductInOffer resolves a field by the first product-id key it finds, which
    // silently rewrote the row's product (a 20' Dry land rate became "Dry"). Opportunity
    // offers legitimately price every product, so they keep the full list.
    function productsForOffer(offer) {
        if (mode === 'opportunities') return products;
        const product = offer?.product || getProductInOffer({ offer, products });
        return product ? [ product ] : products;
    }

    function productsForGroup(offerGroup) {
        if (mode === 'opportunities') return products;
        const groupProducts = _.uniqBy(_.compact((offers || [])
            .filter(o => offerGroup.offers.includes(o.id))
            .map(o => o.product || getProductInOffer({ offer: o, products }))), 'id');
        return _.isEmpty(groupProducts) ? products : groupProducts;
    }

    function setFieldProperty({ groupIndex, sectionIndex, fieldIndex, property, value }) {
        const newOfferGroups = _.cloneDeep(offerGroups);
        const offerGroup = newOfferGroups[groupIndex];

        if (property === 'type') {
            const baseField = offerGroup.base.sections[sectionIndex].offers[0].fields[fieldIndex];
            baseField.type = value;
            migrateFieldValuesForType(baseField, value, productsForGroup(offerGroup));
        } else if (property === 'hidden') {
            offerGroup.base.sections[sectionIndex].offers[0].fields[fieldIndex].hidden = value;
        } else if (property === 'title') {
            offerGroup.base.sections[sectionIndex].offers[0].fields[fieldIndex].title = value;
        } else if (property === 'currency') {
            if (offerGroup.base.sections[sectionIndex].offers[0].fields[fieldIndex].type === 'flat') {
                offerGroup.base.sections[sectionIndex].offers[0].fields[fieldIndex].values.flat =
                    offerGroup.base.sections[sectionIndex].offers[0].fields[fieldIndex].values.flat || { value: 0, currency: value };
            }
            // `string` and `date` entries hold text, not money — giving them a currency
            // flipped the cell to a currency editor, which then wrote its whole
            // { currency, value, formula } object into values.date.value.
            _.forEach(offerGroup.base.sections[sectionIndex].offers[0].fields[fieldIndex].values, (v, k) => { if (k !== 'string' && k !== 'date') v.currency = value; });
        }

        setOfferGroups(newOfferGroups);

        const newOffers = _.cloneDeep(offers);
        newOffers.forEach(offer => {
            if (offerGroup.offers.includes(offer.id)) {
                if (property === 'title') {
                    offer.sections[sectionIndex].offers[0].fields[fieldIndex].title = value;
                } else if (property === 'hidden') {
                    offer.sections[sectionIndex].offers[0].fields[fieldIndex].hidden = value;
                } else if (property === 'type') {
                    const field = offer.sections[sectionIndex].offers[0].fields[fieldIndex];
                    const prevType = field.type;
                    if (prevType === value) return;
                    field.type = value;
                    migrateFieldValuesForType(field, value, productsForOffer(offer));
                } else if (property === 'currency') {
                    if (offer.sections[sectionIndex].offers[0].fields[fieldIndex].type === 'flat') {
                        offer.sections[sectionIndex].offers[0].fields[fieldIndex].values.flat =
                            offer.sections[sectionIndex].offers[0].fields[fieldIndex].values.flat || { value: 0, currency: value };
                    }
                    _.forEach(offer.sections[sectionIndex].offers[0].fields[fieldIndex].values, (v, k) => { if (k !== 'string' && k !== 'date') v.currency = value; });
                }
            }
        });
        setOffers(newOffers);
    }

function setSectionProperty({ groupIndex, sectionIndex, property, value }) {
        const newOfferGroups = _.cloneDeep(offerGroups);
        const offerGroup = newOfferGroups[groupIndex];
        if (property === 'title') offerGroup.base.sections[sectionIndex].title = value;
        if (property === 'order') offerGroup.base.sections[sectionIndex].offers[0].fields = value.map(pos => offerGroup.base.sections[sectionIndex].offers[0].fields[pos]);
        setOfferGroups(newOfferGroups);

        const newOffers = _.cloneDeep(offers);
        newOffers.forEach(offer => {
            if (offerGroup.offers.includes(offer.id)) {
                if (property === 'title') offer.sections[sectionIndex].title = value;
                if (property === 'order') offer.sections[sectionIndex].offers[0].fields = value.map(pos => offer.sections[sectionIndex].offers[0].fields[pos]);
            }
        });
        setOffers(newOffers);
    }

    function setGroupProperty({ groupIndex, property, value }) {
        const newOfferGroups = _.cloneDeep(offerGroups);
        const offerGroup = newOfferGroups[groupIndex];
        if (property === 'title') offerGroup.base.title = value;
        if (property === 'description') offerGroup.base.description = value;
        if (property === 'validity') { offerGroup.base.validFrom = value.validFrom; offerGroup.base.validUntil = value.validUntil; }
        if (property === 'order') offerGroup.base.sections = value.map(pos => offerGroup.base.sections[pos]);
        if (property === 'cargowiseCodes') offerGroup.base.sections.forEach((s, si) => s.offers[0].fields.forEach((f, fi) => f.cargowiseChargeCode = value[si][fi]));
        setOfferGroups(newOfferGroups);

        const newOffers = _.cloneDeep(offers);
        newOffers.forEach(offer => {
            if (offerGroup.offers.includes(offer.id)) {
                if (property === 'title') offer.title = value;
                if (property === 'description') offer.description = value;
                if (property === 'order') offer.sections = value.map(pos => offer.sections[pos]);
                if (property === 'validity') { offer.validFrom = value.validFrom; offer.validUntil = value.validUntil; }
                if (property === 'cargowiseCodes') offer.sections.forEach((s, si) => s.offers[0].fields.forEach((f, fi) => f.cargowiseChargeCode = value[si][fi]));
            }
        });
        setOffers(newOffers);
    }

    function onAddBuyingRate({ rate, rateFieldId, section, opportunityOfferId, opportunityFieldId }) {
        // Convert rate from KG to LBS if needed
        const convertedRate = convertRateKgToLbsIfNeeded({ rate, opportunityProducts: products });
        setBuyingRates(_.uniqBy([...buyingRates, convertedRate], br => br.id));
        
        setOffers(_offers => {
            const newOffers = _.cloneDeep(_offers);
            const { path, offerIndex } = findPathForField({ offers: _offers, offerId: opportunityOfferId, fieldId: opportunityFieldId });
            const product = getProductInOffer({ offer: _offers.find(o => o.id === opportunityOfferId), products });
            const _fieldBuyingRates = _.get(newOffers, `${path}.buyingRates`) || [];
            if (rateFieldId) {
                if (_fieldBuyingRates.find(f => f.rateId === convertedRate.id && f.fieldId === rateFieldId)) return _offers;
                _fieldBuyingRates.push({ rateId: convertedRate.id, fieldId: rateFieldId, opportunityProductId: product.id });
            } else if (section) {
                section.offers[0].fields.forEach(f => {
                    if (f.type === 'string') return;
                    const _rateFieldId = f.id;
                    if (_fieldBuyingRates.find(f => f.rateId === convertedRate.id && f.fieldId === _rateFieldId)) return;
                    _fieldBuyingRates.push({ rateId: convertedRate.id, fieldId: _rateFieldId, opportunityProductId: product.id });
                });
            } else {
                convertedRate.offer.sections.forEach(s => {
                    s.offers[0].fields.forEach(f => {
                        if (f.type === 'string') return;
                        const _rateFieldId = f.id;
                        if (_fieldBuyingRates.find(f => f.rateId === convertedRate.id && f.fieldId === _rateFieldId)) return;
                        _fieldBuyingRates.push({ rateId: convertedRate.id, fieldId: _rateFieldId, opportunityProductId: product.id });
                    });
                });
            }

            _.set(newOffers, `${path}.buyingRates`, _fieldBuyingRates);
            return newOffers;
        })
    }

    function onRemoveBuyingRate({ supplier, opportunityOfferId, opportunityFieldId }) {
        const { path } = findPathForField({ offers, offerId: opportunityOfferId, fieldId: opportunityFieldId });
        const newOffers = _.cloneDeep(offers);

        let fieldBuyingRates = _.get(newOffers, `${path}.buyingRates`) || [];
        fieldBuyingRates = fieldBuyingRates.filter(f => {
            const rate = (buyingRates || []).find(r => r.id === f.rateId);
            return rate.supplier.organization !== supplier;
        });
        _.set(newOffers, `${path}.buyingRates`, fieldBuyingRates);

        setOffers(newOffers);

        const allBuyingRates = _.uniq(_.compact(_.flatten(_.flatten(_.flatten(_.flatten(newOffers.map(o => o.sections.map(s => s.offers.map(so => so.fields.map(f => (f.buyingRates || []).map(br => br.rateId)))))))))));
        setBuyingRates(buyingRates.filter(r => allBuyingRates.includes(r.id)));
    }

    function onShowRates({ opportunityOfferId, opportunityFieldId, supplier }) {
        const { path } = findPathForField({ offers, offerId: opportunityOfferId, fieldId: opportunityFieldId });
        const fieldBuyingRates = _.get(offers, `${path}.buyingRates`) || [];
        const ratesToShow = buyingRates.filter(f => fieldBuyingRates.find(ff => ff.rateId === f.id) && f.supplier.organization === supplier);
        setRatesMoreInfo({ open: true, rates: ratesToShow, emphasize: fieldBuyingRates, supplier, path });
    }

    async function _onAdminDelete(checkboxes) {
        setAdminDeleting('deleting');
        try {
            await onAdminDelete(checkboxes);
            setAdminDeleting('success');
            await new Promise(res => setTimeout(res, 5000));
            setAdminDeleting(null);
        } catch (e) {
            setAdminDeleting('error');
            await new Promise(res => setTimeout(res, 5000));
            setAdminDeleting(null);
        }
    }

    if (_.isNull(offerGroups)) return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <CircularProgress size={36} />
        </Box>
    )

    return (
        <>
            <OfferGroup
                offerGroups={offerGroups} 
                setSelected={_setSelected}
                mode={mode}
                setModal={setModal}
                setSortModal={setSortModal}
                setCargowiseCodes={setCargowiseCodes}
                onCreateTemplate={onCreateTemplate}
                locked={locked}
                buyingRates={buyingRates}        
                onDeleteGroup={onDeleteGroup}
                setAddRows={setAddRows}
                setEditable={setEditable}
                tableContentRef={tableContentRef}
                cellWidth={cellWidth}
                setField={setField}
                setFieldProperty={setFieldProperty}
                setSectionProperty={setSectionProperty}
                setGroupProperty={setGroupProperty}
                setBuyingRates={setBuyingRates}
                setOffers={setOffers}
                offers={offers}
                products={products}
                routes={routes}
                seaPorts={seaPorts}
                unlocode={unlocode}
                suppliers={suppliers}
                transportationMethod={transportationMethod}
                minimizedSearch={minimizedSearch}
                fontSize={fontSize}
                setRatesMoreInfo={setRatesMoreInfo}
                cargowiseCodes={cargowiseCodes}
                editable={editable}
                selected={selected}
                highlighted={highlighted}
                onNewSection={onNewSection}
                onDeleteSection={onDeleteSection}
                onNewField={onNewField}
                onDeleteField={onDeleteField}
                onChangeCurrency={onChangeCurrency}
                onHideOffer={onHideOffer}
                onEditLine={locked ? null : onEditLine}
                onAddRate={onAddRate}
                onExportGroup={onExportGroup}
                onAddBelow={onAddBelow}
                onDuplicate={onDuplicate}
                onAddBuyingRate={onAddBuyingRate}
                onRemoveBuyingRate={onRemoveBuyingRate}
                onShowRates={onShowRates}
                savingSuccess={savingSuccess}
                savingError={savingError}
                savingErrorMessages={savingErrorMessages}
                saving={saving}
                onSave={onSave}
                dirty={dirty}
                onDeleteOffer={_onDeleteOffer}
                me={me}
                checkboxes={checkboxes}
                setCheckboxes={setCheckboxes}
                exchangeRates={exchangeRates}
                org={org}
            />

            {offerGroups && (mode === 'rates' || mode === 'tariff' || mode === 'opportunities') && (
                <AddNewGroup 
                    org={org}
                    mode={mode}
                    transportationMethod={transportationMethod}
                    offerGroups={offerGroups}
                    setOfferGroups={newGroups => {
                        setOfferGroups(newGroups);
                        setAddRows({ open: true, groupIndex: newGroups.length - 1 });
                    }}
                    offers={offers}
                    setOffers={setOffers}
                    baseRoutes={routes}
                />
            )}

            {offerGroups && me?.email === 'admin@monada.ai' && (mode === 'rates' || mode === 'tariff') && (
                <Box sx={{ paddingTop: '24px' }}>
                    <Button 
                        variant='contained' 
                        color='error' 
                        disabled={checkboxes.length === 0 || adminDeleting === 'deleting'}
                        onClick={() => _onAdminDelete(checkboxes)}
                    >
                        {adminDeleting === 'deleting' ? <CircularProgress size={18} /> : 
                            adminDeleting === 'success' ? <FontAwesomeIcon icon={faCheck} /> :
                            adminDeleting === 'error' ? <FontAwesomeIcon icon={faExclamationCircle} /> :
                            `Delete ${checkboxes.length} rate(s)`}
                    </Button>
                </Box>
            )}

            {editLine && (
                <OpportunityContractEditLineModal
                    open={true}
                    line={editLine.line}
                    products={products}
                    transportationMethod={transportationMethod}
                    seaPorts={seaPorts}
                    unlocode={unlocode}
                    onDone={onApplyEditLine}
                    onClose={() => setEditLine(null)}
                />
            )}
            <OpportunityContractAddRowsModal
                organization={org}
                open={addRows.open}
                transportationMethod={transportationMethod}
                seaPorts={seaPorts} 
                unlocode={unlocode}
                products={products}
                onClose={() => setAddRows({ open: false })}
                onDone={({ routes, selectedProducts }) => {
                    onAddRoutes({ routes, selectedProducts, groupIndex: addRows.groupIndex, offerId: addRows.offerId });
                    setAddRows({ open: false });
                }}
            />

            <OpportunityTitleEditModal
                modal={{
                    ...modal,
                    onOk: value => setGroupProperty({ groupIndex: modal.groupIndex, property: modal.property, value })
                }} 
                setModal={setModal}
            />

            <OpportunityCargowiseCodesModal
                offer={cargowiseCodes ? { sections: offerGroups[cargowiseCodes.groupIndex].base.sections } : null}
                onCancel={() => setCargowiseCodes(null)}
                onDone={offer => {
                    const value = offer.sections.map(s => s.offers[0].fields.map(f => f.cargowiseChargeCode));
                    setGroupProperty({ groupIndex: cargowiseCodes.groupIndex, property: 'cargowiseCodes', value })
                    setCargowiseCodes(null);
                }}
            />

            <TitleableReorders
                modal={sortModal} 
                setModal={setSortModal} 
                onChange={v => {
                    if (_.isUndefined(sortModal.sectionIndex)) {
                        const base = offerGroups[sortModal.groupIndex].base;
                        const order = v.map(a => _.findIndex(base.sections, s => a.id === s.id));
                        setGroupProperty({ groupIndex: sortModal.groupIndex, property: 'order', value: order });
                    } else {
                        const base = offerGroups[sortModal.groupIndex].base;
                        const order = v.map(a => _.findIndex(base.sections[sortModal.sectionIndex].offers[0].fields, s => a.id === s.id));
                        setSectionProperty({ groupIndex: sortModal.groupIndex, sectionIndex: sortModal.sectionIndex, property: 'order', value: order });
                    }
                }}
            />

            <Drawer
                anchor={'right'}
                open={ratesMoreInfo.open}
                onClose={() => setRatesMoreInfo({...ratesMoreInfo, open: false})}
                disableEnforceFocus={true}
                hideBackdrop={true}
                variant='persistent'
                PaperProps= {{ 
                    sx: { background: '#fafbfc' }
                }}
            >
                <RatesDetailsWrapper
                    organization={org}
                    rates={ratesMoreInfo.rates}
                    initialEmphasize={ratesMoreInfo.emphasize}
                    minimizedSearch={minimizedSearch}
                    exchangeRates={exchangeRates}
                    onStartDragging={() => setRatesMoreInfo({...ratesMoreInfo, open: false})}
                    onEndDragging={() => setRatesMoreInfo({...ratesMoreInfo, open: true})}
                    onClose={() => setRatesMoreInfo({...ratesMoreInfo, open: false})}
                    onSave={newFieldBuyingRates => {
                        const { path } = ratesMoreInfo;
                        const fieldBuyingRates =  _.get(offers, `${path}.buyingRates`) || [];
                        const noSupplierFieldBuyingRates = fieldBuyingRates.filter(f => !ratesMoreInfo.rates.find(rr => f.rateId === rr.id));
                        setOffers(_offers => {
                            const newOffers = _.cloneDeep(_offers);
                            _.set(newOffers, `${path}.buyingRates`, newFieldBuyingRates);
                            return newOffers
                        });
                        setRatesMoreInfo({...ratesMoreInfo, open: false});
                    }} 
                    transportationMethod={transportationMethod}
                />
            </Drawer>

            <CurrencyDialog
                open={currencySelector.open}
                onClose={() => setCurrencySelector({ open: false })}
                onCurrency={(currency) => {
                    currencySelector.onDone({ currency });
                    setCurrencySelector({ open: false });
                }}
                defaultCurrency={org.defaults?.currency}
                extraCurrencies={org.defaults?.extraCurrencies}
            />
        </>
    )
}

export default ContractBuilder;

function OfferGroup(props) {
    const {
        offerGroups, mode, setModal, setSortModal, setCargowiseCodes, onCreateTemplate, locked, buyingRates,
        onDeleteGroup, setAddRows, setEditable, setSelected, tableContentRef, cellWidth, setField, setFieldProperty, setSectionProperty, 
        setGroupProperty, setBuyingRates, setOffers, offers, products, routes, seaPorts, suppliers, transportationMethod, 
        minimizedSearch, fontSize, setRatesMoreInfo, cargowiseCodes, editable, selected, highlighted, onNewSection, onDeleteSection, onNewField, onDeleteField, onChangeCurrency,
        onHideOffer, onEditLine, onAddRate, onAddBelow, onExportGroup, onDuplicate, onAddBuyingRate, onRemoveBuyingRate, onShowRates, savingSuccess, savingError, savingErrorMessages = {}, saving, onSave,
        dirty, onDeleteOffer, me, checkboxes, setCheckboxes, exchangeRates, org,
    } = props;
    const theme = useTheme();

    // State for inline editing
    const [editingTitle, setEditingTitle] = useState(null); // groupIndex when editing title
    const [editingDescription, setEditingDescription] = useState(null); // groupIndex when editing description
    const [editTitleValue, setEditTitleValue] = useState('');
    const [editDescriptionValue, setEditDescriptionValue] = useState('');

    // Sync edit values when not editing
    useEffect(() => {
        if (editingTitle === null && editingDescription === null) {
            // Reset values when not editing
            setEditTitleValue('');
            setEditDescriptionValue('');
        }
    }, [editingTitle, editingDescription]);

    const BASE_LEFT = mode === 'rates' && me?.email === 'admin@monada.ai' ? 120 : 60;

    return (offerGroups || []).map((group, groupIndex) => {
        const base = group.base;

        return (
            <Paper id='contractbuilder' key={group.id} sx={{ padding: mode.startsWith('rates') ? '0px' : '12px', border:`1px solid ${theme.palette.border1}`, marginBottom: '12px', ...(mode.startsWith('rates') ? { background: 'transparent', border: 'none' } : {}) }} elevation={0} >
                {mode === 'opportunities' && (
                    <Box sx={{ paddingBottom: '36px' }}>
                        <Box style={{ display: 'flex', alignItems: 'center', padding: '12px 12px 0px 0px' }}>
                            {editingTitle === groupIndex && !locked ? (
                                <TextField
                                    autoFocus
                                    value={editTitleValue}
                                    onChange={(e) => setEditTitleValue(e.target.value)}
                                    onBlur={() => {
                                        setGroupProperty({ groupIndex, property: 'title', value: editTitleValue });
                                        setEditingTitle(null);
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.target.blur();
                                        } else if (e.key === 'Escape') {
                                            setEditTitleValue(base.title || '');
                                            setEditingTitle(null);
                                        }
                                    }}
                                    variant='standard'
                                    sx={{ flex: 1, minWidth: '200px', '& .MuiInputBase-root': { fontSize: '20px', fontWeight: 800 } }}
                                />
                            ) : (
                                <>
                                    <Box 
                                        style={{ 
                                            fontWeight: 800, 
                                            fontSize: '20px',
                                            cursor: locked ? 'default' : 'pointer',
                                            textDecoration: locked ? 'none' : 'underline',
                                            textDecorationColor: 'transparent',
                                            transition: 'text-decoration-color 0.2s'
                                        }}
                                        onMouseEnter={(e) => {
                                            if (!locked) {
                                                e.currentTarget.style.textDecorationColor = theme.palette.primary.main;
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.textDecorationColor = 'transparent';
                                        }}
                                        onClick={() => {
                                            if (!locked) {
                                                setEditTitleValue(base.title || '');
                                                setEditingTitle(groupIndex);
                                            }
                                        }}
                                    >
                                        {base.title || <i style={{ opacity: 0.5 }}>New Group</i>}
                                    </Box>
                                </>
                            )}
                            {!locked && (
                                <ContextMenu
                                    anchor='right'
                                    options={[
                                        { 
                                            key: 1, 
                                            label: 'Reorder sections', 
                                            onClick: () => setSortModal({ open: true, title: 'Sort Sections', items: base.sections, groupIndex, }),
                                            icon: <FontAwesomeIcon icon={faShuffle} /> 
                                        },
                                        { 
                                            key: 'divider-0',
                                        },
                                        { 
                                            key: 2,
                                            label: 'Delete Group',
                                            icon: <FontAwesomeIcon icon={faTrash} />,
                                            onClick: () => onDeleteGroup({ groupIndex })
                                        },
                                        { 
                                            key: 'divider-1',
                                        },
                                        { 
                                            key: 3,
                                            label: (offerGroups[groupIndex].offers || []).length === 0 ? 'Create template from group (at least one offer required)' : 'Create template from group',
                                            icon: <FontAwesomeIcon icon={faFileCirclePlus} />,
                                            onClick: () => onCreateTemplate({ offer: offers.find(o => o.id === offerGroups[groupIndex].offers[0]) }),
                                            disabled: (offerGroups[groupIndex].offers || []).length === 0
                                        },
                                        { 
                                            key: 1, 
                                            label: 'Set up Cargowise codes', 
                                            onClick: () => setCargowiseCodes({ groupIndex }),
                                            icon: <FontAwesomeIcon icon={faBarcodeRead} /> 
                                        },
                                    ]}
                                />
                            )}
                            {locked && (
                                <ContextMenu
                                    anchor='right'
                                    options={[
                                        { 
                                            key: 1, 
                                            label: 'Set up Cargowise codes', 
                                            onClick: () => setCargowiseCodes({ groupIndex }),
                                            icon: <FontAwesomeIcon icon={faBarcodeRead} /> 
                                        },
                                    ]}
                                />
                            )}
                        </Box>
                        <Box style={{ display: 'flex', alignItems: 'center' }}>
                            {editingDescription === groupIndex && !locked ? (
                                <TextField
                                    autoFocus
                                    value={editDescriptionValue}
                                    onChange={(e) => setEditDescriptionValue(e.target.value)}
                                    onBlur={() => {
                                        setGroupProperty({ groupIndex, property: 'description', value: editDescriptionValue });
                                        setEditingDescription(null);
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.target.blur();
                                        } else if (e.key === 'Escape') {
                                            setEditDescriptionValue(base.description || '');
                                            setEditingDescription(null);
                                        }
                                    }}
                                    variant='standard'
                                    placeholder='You can add a description to the group here.'
                                    sx={{ flex: 1, minWidth: '200px' }}
                                />
                            ) : (
                                <>
                                    <Box
                                        style={{
                                            fontWeight: 400,
                                            fontSize: '13px',
                                            color: theme.palette.text.secondary,
                                            cursor: locked ? 'default' : 'pointer',
                                            textDecoration: locked ? 'none' : 'underline',
                                            textDecorationColor: 'transparent',
                                            transition: 'text-decoration-color 0.2s',
                                            fontStyle: !base.description ? 'italic' : 'normal'
                                        }}
                                        onMouseEnter={(e) => {
                                            if (!locked) {
                                                e.currentTarget.style.textDecorationColor = theme.palette.primary.main;
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.textDecorationColor = 'transparent';
                                        }}
                                        onClick={() => {
                                            if (!locked) {
                                                setEditDescriptionValue(base.description || '');
                                                setEditingDescription(groupIndex);
                                            }
                                        }}
                                    >
                                        {base.description || <i style={{ opacity: 0.5 }}>You can add a description to the group here.</i>}
                                    </Box>
                                </>
                            )}
                        </Box>
                        <Box sx={{ padding: '12px 0px 0px 0px' }}>
                            <ValiditySpecialInputField
                                icon={<FontAwesomeIcon icon={faCalendar} />}
                                emptyStateText={'Validity'}
                                onOpenModal={() => {
                                    setModal({
                                        title: 'Set Group\'s Validity',
                                        label: 'Validiy',
                                        value: { validFrom: base.validFrom, validUntil: base.validUntil },
                                        groupIndex,
                                        property: 'validity',
                                        inputType: 'validity',
                                        open: true,
                                    });
                                }}
                                disabled={locked}
                                value={{ validFrom: base.validFrom, validUntil: base.validUntil }}
                            />
                        </Box>
                    </Box>
                )}

                <Box sx={{ position: 'relative' }}>
                    <TableContainer ref={e => tableContentRef.current[groupIndex] = e} sx={{ background: theme.palette.panel1 }}>
                        <Table sx={{ border: `1px solid ${theme.palette.border1}`, borderCollapse: 'separate', fontSize }} >
                            <TableHead>
                                <TableRow>
                                    <TableCell colSpan={mode === 'opportunities' ? 4 : mode === 'tariff' ? 4 : mode === 'rates-import' ? 5 : me?.email === 'admin@monada.ai' ? 6 : 5} sx={{ position: 'sticky', left: '0px', width: '60px', background: '#f5f7fa', zIndex: 2, borderRight: `1px solid ${theme.palette.border1}`, padding: '8px 12px', fontWeight: 600 }} />
                                    {mode.startsWith('rates') && <TableCell colSpan={3} sx={{ fontSize: 'inherit', textAlign: 'center', background: '#f5f7fa', fontWeight: 600, padding: '8px 12px' }}>Validity and Type</TableCell>}
                                    {mode === 'tariff' && <TableCell colSpan={2} sx={{ fontSize: 'inherit', textAlign: 'center', background: '#f5f7fa', fontWeight: 600, padding: '8px 12px' }}>Validity</TableCell>}
                                    <TableCell colSpan={2} sx={{ fontSize: 'inherit', textAlign: 'center', borderLeft: `1px solid ${theme.palette.border1}`, background: '#f5f7fa', fontWeight: 600, padding: '8px 12px' }}>
                                        Route Information
                                    </TableCell>
                                    {base.sections.map((section, sectionIndex) => (
                                        <Cell
                                            key={`base-${section.id}`}
                                            type='sectiontitle'
                                            value={section.title}
                                            cleanValue={section.title}
                                            defaultValue={'New Section'}
                                            colSpan={section.offers[0].fields.length}
                                            path={`${section.id}`}
                                            editable={editable}
                                            selected={selected}
                                            highlighted={highlighted}
                                            setEditable={setEditable}
                                            setSelected={setSelected}
                                            setValue={value => setSectionProperty({ groupIndex, sectionIndex, property: 'title', value })}
                                            disabledDelete={base.sections.length < 2}
                                            disabled={mode !== 'rates' && mode !== 'tariff' && mode !== 'opportunities'}
                                            section={section}
                                            cellWidth={cellWidth}
                                            transportationMethod={transportationMethod}
                                            sx={{ background: '#f5f7fa', fontWeight: 600, textAlign: 'center' }}
                                            onTitleAction={(action) => {
                                                switch (action) {
                                                    case 'addfield':
                                                        onNewSection({ groupIndex, sectionIndex });
                                                        break;
                                                    case 'deletefield':
                                                        onDeleteSection({ groupIndex, sectionIndex });
                                                        break;
                                                    case 'reorder':
                                                        setSortModal({ open: true, title: 'Sort Fields', items: section.offers[0].fields, groupIndex, sectionIndex });
                                                        break;
                                                }
                                            }}
                                            org={org}
                                        />
                                    ))}
                                </TableRow>
                                <TableRow>
                                    {mode === 'rates' && me?.email === 'admin@monada.ai' && (
                                        <TableCell sx={{ width: '60px', minWidth: '60px', maxWidth: '60px', position: 'sticky', left: '0px', background: '#f5f7fa', zIndex: 2, padding: '8px 12px', fontWeight: 600 }}>
                                            <Checkbox
                                                sx={{ padding: '0px' }}
                                                checked={_.intersection(checkboxes, group.offers).length === group.offers.length}
                                                indeterminate={
                                                    _.intersection(checkboxes, group.offers).length > 0 &&
                                                    _.intersection(checkboxes, group.offers).length < group.offers.length}
                                                onChange={() => {
                                                    if (checkboxes.length === group.offers.length) {
                                                        setCheckboxes(_.difference(checkboxes, group.offers));
                                                    } else {
                                                        setCheckboxes(c => _.uniq([...c, ...group.offers]));
                                                    }
                                                }}
                                            />
                                        </TableCell>
                                    )}
                                    <TableCell sx={{ width: '60px', minWidth: '60px', maxWidth: '60px', position: 'sticky', left: mode === 'rates' && me?.email === 'admin@monada.ai' ? '60px' : '0px', background: '#f5f7fa', zIndex: 1, maxWidth: '60px', minWidth: '60px', padding: '8px 12px', fontWeight: 600 }} />
                                    <TableCell sx={{ fontSize: 'inherit', border: `1px solid ${theme.palette.border1}`, width: cellWidth, minWidth: cellWidth, maxWidth: cellWidth, position: 'sticky', left: BASE_LEFT, background: '#f5f7fa', zIndex: 2, padding: '8px 12px', fontWeight: 600, textAlign: 'left' }}>
                                        Port of Origin
                                    </TableCell>
                                    <TableCell sx={{ fontSize: 'inherit', border: `1px solid ${theme.palette.border1}`, width: cellWidth, minWidth: cellWidth, maxWidth: cellWidth, position: 'sticky', left: BASE_LEFT + cellWidth, background: '#f5f7fa', zIndex: 2, padding: '8px 12px', fontWeight: 600, textAlign: 'left' }}>
                                        Port of Destination
                                    </TableCell>
                                    <TableCell sx={{ fontSize: 'inherit', border: `1px solid ${theme.palette.border1}`, width: cellWidth, minWidth: cellWidth, maxWidth: cellWidth, position: 'sticky', left: BASE_LEFT + 2 * cellWidth, background: '#f5f7fa', zIndex: 2, padding: '8px 12px', fontWeight: 600, textAlign: 'left' }}>
                                        Product
                                    </TableCell>
                                    {mode.startsWith('rates') && (
                                        <TableCell sx={{ fontSize: 'inherit', border: `1px solid ${theme.palette.border1}`, width: cellWidth, minWidth: cellWidth, maxWidth: cellWidth, position: 'sticky', left: BASE_LEFT + 3 * cellWidth, background: '#f5f7fa', zIndex: 2, padding: '8px 12px', fontWeight: 600, textAlign: 'left' }}>
                                            Supplier (Org)
                                        </TableCell>
                                    )}
                                    {(mode.startsWith('rates') || mode === 'tariff') && (
                                        <TableCell sx={{ fontSize: 'inherit', border: `1px solid ${theme.palette.border1}`, width: cellWidth, minWidth: cellWidth, maxWidth: cellWidth, background: '#f5f7fa', zIndex: 2, padding: '8px 12px', fontWeight: 600, textAlign: 'left' }}>
                                            Valid From
                                        </TableCell>
                                    )}
                                    {(mode.startsWith('rates') || mode === 'tariff') && (
                                        <TableCell sx={{ fontSize: 'inherit', border: `1px solid ${theme.palette.border1}`, width: cellWidth, minWidth: cellWidth, maxWidth: cellWidth, background: '#f5f7fa', zIndex: 2, padding: '8px 12px', fontWeight: 600, textAlign: 'left' }}>
                                            Valid Until
                                      </TableCell>
                                    )}
                                    {mode.startsWith('rates') && (
                                        <TableCell sx={{ fontSize: 'inherit', border: `1px solid ${theme.palette.border1}`, width: cellWidth, minWidth: cellWidth, maxWidth: cellWidth, background: '#f5f7fa', zIndex: 2, padding: '8px 12px', fontWeight: 600, textAlign: 'left' }}>
                                            Type
                                        </TableCell>
                                    )}
                                    <TableCell sx={{ fontSize: 'inherit', border: `1px solid ${theme.palette.border1}`, background: '#f5f7fa', padding: '8px 12px', fontWeight: 600, textAlign: 'left' }}>
                                        Transit Time
                                    </TableCell>
                                    <TableCell sx={{ fontSize: 'inherit', border: `1px solid ${theme.palette.border1}`, background: '#f5f7fa', padding: '8px 12px', fontWeight: 600, textAlign: 'left' }}>
                                        Transshipment
                                    </TableCell>
                                    {_.flatten(base.sections.map((section, sectionIndex) => {
                                        return section.offers[0].fields.map((field, fieldIndex) => (
                                            <Cell
                                                key={field.id}
                                                type='fieldtitle'
                                                value={field.title}
                                                cleanValue={field.title}
                                                hidden={field.hidden}
                                                defaultValue={'New Field'}
                                                path={`${section.id}:${field.id}`}
                                                disabledDelete={section.offers[0].fields.length < 2}
                                                disabled={mode !== 'rates' && mode !== 'tariff' && mode !== 'opportunities'}
                                                editable={editable}
                                                selected={selected}
                                                highlighted={highlighted}
                                                setEditable={setEditable}
                                                setSelected={setSelected}
                                                setValue={value => setFieldProperty({ groupIndex, sectionIndex, fieldIndex, property: 'title', value })}
                                                field={field}
                                                cellWidth={cellWidth}
                                                transportationMethod={transportationMethod}
                                                sx={{ background: '#f5f7fa', fontWeight: 600, textAlign: 'center' }}
                                                onTitleAction={(action) => {
                                                    if (FieldTypes[transportationMethod].find(f => f.value === action)) {
                                                        setFieldProperty({ groupIndex, sectionIndex, fieldIndex, property: 'type', value: action });
                                                    } else {
                                                        switch (action) {
                                                            case 'datecell':
                                                                setFieldProperty({ groupIndex, sectionIndex, fieldIndex, property: 'type', value: 'date' });
                                                                break;
                                                            case 'fieldhide':
                                                                setFieldProperty({ groupIndex, sectionIndex, fieldIndex, property: 'hidden', value: !field.hidden });
                                                                setEditable(null);
                                                                break;
                                                            case 'addfield':
                                                                onNewField({ groupIndex, sectionIndex, fieldIndex });
                                                                break;
                                                            case 'deletefield':
                                                                onDeleteField({ groupIndex, sectionIndex, fieldIndex });
                                                                break;
                                                            case 'changecurrency':
                                                                onChangeCurrency({ groupIndex, sectionIndex, fieldIndex });
                                                                break;
                                                        }
                                                    }
                                                }}
                                                org={org}
                                            />
                                        ));
                                    }))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {group.offers.map(id => offers.find(o => o.id === id)).map(offer => {
                                    if (!offer) return null;
                                    const offerIndex = offers.findIndex(o => o.id === offer.id);
                                    // Cell paths are `${offer.id}:${field}`, so this tells us the row
                                    // has a focused cell whose typed value is still in the cell's local
                                    // state and hasn't reached the offer yet.
                                    const editingThisRow = _.isString(editable) && editable.startsWith(`${offer.id}:`);
                                    const product = getProductInOffer({ offer, products }) || products[group.offers.indexOf(offer.id) % products.length];
                                    const { multiplier = 1, multiplierText = ''} = product ? getMultiplier({ product, transportationMethod, allProducts: [product], shorten: true, round: org.airRoundChargeableWeight }) : {};
                                    const rowIndex = group.offers.indexOf(offer.id);
                                    return (
                                        <TableRow
                                            sx={{
                                                opacity: offer.hidden ? 0.5 : 1,
                                                textDecoration: offer.deleted ? 'line-through' : 'none',
                                                backgroundColor: rowIndex % 2 === 0 ? '#ffffff' : '#f8f9fa',
                                                '&:hover': {
                                                    backgroundColor: rowIndex % 2 === 0 ? '#f0f4f8' : '#e8ecf0',
                                                    transition: 'background-color 0.2s ease'
                                                }
                                            }}
                                        >
                                            {mode === 'rates' && me?.email === 'admin@monada.ai' && (
                                                <TableCell sx={{ width: '60px', minWidth: '60px', maxWidth: '60px', position: 'sticky', left: '0px', background: rowIndex % 2 === 0 ? '#ffffff' : '#f8f9fa', zIndex: 1, maxWidth: '60px', minWidth: '60px', padding: '8px 12px' }}>
                                                    <Checkbox
                                                        sx={{ padding: '0px' }}
                                                        checked={checkboxes.includes(offer.id)}
                                                        onChange={() => setCheckboxes(_.xor(checkboxes, [offer.id]))}
                                                    />
                                                </TableCell>
                                            )}
                                            <TableCell sx={{ position: 'sticky', left: mode === 'rates' && me?.email === 'admin@monada.ai' ? '60px' : '0px', background: rowIndex % 2 === 0 ? '#ffffff' : '#f8f9fa', textAlign: 'center', zIndex: 1, padding: '8px 12px' }}>
                                                {mode === 'opportunities' && (
                                                    <ContextMenu
                                                        anchor='right'
                                                        options={[
                                                            { 
                                                                key: 1, 
                                                                label: offer.hidden ? 'Show route' : 'Hide route', 
                                                                onClick: () => onHideOffer({ offerId: offer.id }),
                                                                icon: <FontAwesomeIcon icon={faEye} />,
                                                            },
 
                                                            ...(onEditLine ? [{
                                                                key: 'edit-line',
                                                                label: 'Edit route & product',
                                                                onClick: () => onEditLine({ offerId: offer.id }),
                                                                icon: <FontAwesomeIcon icon={faEdit} />,
                                                            }] : []),
                                                            { 
                                                                key: 2, 
                                                                label: 'Duplicate', 
                                                                onClick: () => onDuplicate({ groupIndex, offerId: offer.id }),
                                                                icon: <FontAwesomeIcon icon={faClone} />,
                                                            },
                                                            { 
                                                                key: 3, 
                                                                label: 'Add route(s) below', 
                                                                onClick: () => onAddBelow({ groupIndex, offerId: offer.id }),
                                                                icon: <FontAwesomeIcon icon={faCirclePlus} />,
                                                            },
                                                            { 
                                                                key: 3, 
                                                                label: 'Delete route', 
                                                                onClick: () => onDeleteOffer({ offerId: offer.id }),
                                                                icon: <FontAwesomeIcon icon={faTrash} />,
                                                                sx: { color: red[500] }
                                                            },
                                                        ]}
                                                        icon={offer.hidden ? faEyeSlash : null}
                                                    />
                                                )}
                                                {(offer.deleted ? null : (
                                                    <>
                                                        {(mode === 'rates' || mode === 'tariff') && (
                                                            <Tooltip title={savingErrorMessages[offer.id] || ''} arrow>
                                                                <span>
                                                                    <IconButton
                                                                        color={savingSuccess.includes(offer.id) ? 'success' : savingError.includes(offer.id) ? 'error' : 'primary'}
                                                                        disabled={(!dirty.includes(offer.id) && !editingThisRow) || saving.includes(offer.id)}
                                                                        onClick={() => {
                                                                            if (mode !== 'rates-import') {
                                                                                // Commit the focused cell first: its value lives in the cell's
                                                                                // local state until it blurs, so saving straight from a focused
                                                                                // field would otherwise send the pre-edit value.
                                                                                if (editingThisRow) setEditable(null);
                                                                                onSave(offer);
                                                                            }
                                                                        }}
                                                                    >
                                                                        {saving.includes(offer.id) ? <CircularProgress size={18} /> : savingSuccess.includes(offer.id) ? <FontAwesomeIcon icon={faCheck} /> : savingError.includes(offer.id) ? <FontAwesomeIcon icon={faXmark} /> : mode === 'tariff' || mode === 'rates' ? <FontAwesomeIcon icon={faSave} /> : null}
                                                                    </IconButton>
                                                                </span>
                                                            </Tooltip>
                                                        )}
                                                        {mode !== 'rates' && mode !== 'tariff' && mode !== 'opportunities' && (
                                                            <Box sx={{ color: savingSuccess.includes(offer.id) ? green[500] : savingError.includes(offer.id) ? red[500] : grey[500] }}>
                                                                { saving.includes(offer.id) ? <CircularProgress size={18} /> : savingSuccess.includes(offer.id) ? <FontAwesomeIcon icon={faCheck} /> : savingError.includes(offer.id) ? <FontAwesomeIcon icon={faXmark} /> : mode === 'tariff' || mode === 'rates' ? <FontAwesomeIcon icon={faSave} /> : null }
                                                            </Box>
                                                        )}
                                                    </>
                                                ))}
                                                {(mode !== 'tariff' || offer.deleted) ? null : (
                                                    <IconButton
                                                        color='error'
                                                        onClick={() => onDeleteOffer({ offerId: offer.id })} 
                                                    >
                                                        <FontAwesomeIcon icon={faTrash} />
                                                    </IconButton>
                                                )}
                                                {mode === 'rates-import' && savingSuccess.includes(offer.id) && me.email === 'admin@monada.ai' && (
                                                    <IconButton
                                                        color='error'
                                                        onClick={() => onDeleteOffer({ offerId: offer.id })} 
                                                    >
                                                        <FontAwesomeIcon icon={faTrash} />
                                                    </IconButton>
                                                )}
                                            </TableCell>
                                            <Cell
                                                type='route'
                                                disabled
                                                value={offer.routes[0].source?.text || 'Everywhere'}
                                                cleanValue={offer.routes[0].source?.text || 'Everywhere'}
                                                path={`${offer.id}:source`}
                                                sx={{ position: 'sticky', left: BASE_LEFT, background: rowIndex % 2 === 0 ? '#ffffff' : '#f8f9fa', zIndex: 2, textAlign: 'left' }}
                                                cellWidth={cellWidth}
                                                org={org}
                                            />
                                            <Cell
                                                type='route'
                                                disabled
                                                value={offer.routes[0].destination?.text || 'Everywhere'}
                                                cleanValue={offer.routes[0].destination?.text || 'Everywhere'}
                                                path={`${offer.id}:destination`}
                                                sx={{ position: 'sticky', left: BASE_LEFT + cellWidth, background: rowIndex % 2 === 0 ? '#ffffff' : '#f8f9fa', zIndex: 2, textAlign: 'left' }}
                                                cellWidth={cellWidth}
                                                org={org}
                                            />
                                            <TableCell
                                                sx={{ fontSize: 'inherit', border: `1px solid ${theme.palette.border1}`, minWidth: cellWidth, padding: '8px 12px', position: 'sticky', left: BASE_LEFT + 2 * cellWidth, background: rowIndex % 2 === 0 ? '#ffffff' : '#f8f9fa', zIndex: 2, textAlign: 'left',
                                                    ...(mode === 'opportunities' && onEditLine ? { cursor: 'pointer', '&:hover': { textDecoration: 'underline dotted' } } : {}) }}
                                                className='nonotch'
                                                title={mode === 'opportunities' && onEditLine ? 'Edit route & product' : undefined}
                                                onClick={mode === 'opportunities' && onEditLine ? (e => { if (e.target.closest('input, button')) return; onEditLine({ offerId: offer.id }); }) : undefined}
                                            >
                                                {(product.type === 'LCL' || product.type === 'By Volume or Weight (sea)') ? (
                                                    <>
                                                        LCL{product.dangerous ? ' (Dangerous)' : ''}
                                                        <DensityRatioInline
                                                            value={product.densityRatio || 1000}
                                                            onChange={(newVal) => {
                                                                product.densityRatio = newVal;
                                                                setOffers([...offers]);
                                                            }}
                                                        />
                                                    </>
                                                ) : `${product.type}${product.dangerous ? ' (Dangerous)' : ''}`}
                                            </TableCell>
                                            {mode.startsWith('rates') && (
                                                <Cell
                                                    type='string'
                                                    value={offer.supplier?.organization}
                                                    cleanValue={offer.supplier?.organization}
                                                    path={`${offer.id}:supplier-org`}
                                                    editable={editable}
                                                    selected={selected}
                                                    highlighted={highlighted}
                                                    setEditable={setEditable}
                                                    setSelected={setSelected}
                                                    setValue={value => setField({ path: `[${offerIndex}].supplier.organization`, value })}
                                                    sx={{ position: 'sticky', left: BASE_LEFT + 3 * cellWidth, background: rowIndex % 2 === 0 ? '#ffffff' : '#f8f9fa', zIndex: 2, textAlign: 'left' }}
                                                    cellWidth={cellWidth}
                                                    org={org}
                                                />
                                            )}
                                            {(mode.startsWith('rates') || mode === 'tariff') && (
                                                <Cell
                                                    type='date'
                                                    value={offer.validFrom}
                                                    cleanValue={offer.validFrom}
                                                    path={`${offer.id}:validFrom`}
                                                    editable={editable}
                                                    selected={selected}
                                                    highlighted={highlighted}
                                                    setEditable={setEditable}
                                                    setSelected={setSelected}
                                                    setValue={value => setField({ path: `[${offerIndex}].validFrom`, value })}
                                                    cellWidth={cellWidth}
                                                />
                                            )}
                                            {(mode.startsWith('rates') || mode === 'tariff') && (
                                                <Cell
                                                    type='date'
                                                    value={offer.validUntil}
                                                    cleanValue={offer.validUntil}
                                                    path={`${offer.id}:validUntil`}
                                                    editable={editable}
                                                    selected={selected}
                                                    highlighted={highlighted}
                                                    setEditable={setEditable}
                                                    setSelected={setSelected}
                                                    setValue={value => setField({ path: `[${offerIndex}].validUntil`, value })}
                                                    cellWidth={cellWidth}
                                                    org={org}
                                                />
                                            )}
                                            {mode.startsWith('rates') && (
                                                <Cell
                                                    type='string'
                                                    value={_.capitalize(offer.type)}
                                                    cleanValue={_.capitalize(offer.type)}
                                                    path={`${offer.id}:type`}
                                                    editable={editable}
                                                    selected={selected}
                                                    highlighted={highlighted}
                                                    setEditable={setEditable}
                                                    setSelected={setSelected}
                                                    setValue={value => setField({ path: `[${offerIndex}].type`, value: value.toLowerCase() })}
                                                    cellWidth={cellWidth}
                                                />
                                            )}
                                            <Cell
                                                type='string'
                                                value={offer.transitTime}
                                                cleanValue={offer.transitTime}
                                                path={`${offer.id}:transitTime`}
                                                editable={editable}
                                                selected={selected}
                                                highlighted={highlighted}
                                                setEditable={setEditable}
                                                setSelected={setSelected}
                                                setValue={value => setField({ path: `[${offerIndex}].transitTime`, value })}
                                                sx={{ textAlign: 'left' }}
                                                cellWidth={cellWidth}
                                                org={org}
                                            />
                                            <Cell
                                                type='string'
                                                value={offer.transshipment}
                                                cleanValue={offer.transshipment}
                                                path={`${offer.id}:transshipment`}
                                                editable={editable}
                                                selected={selected}
                                                highlighted={highlighted}
                                                setEditable={setEditable}
                                                setSelected={setSelected}
                                                setValue={value => setField({ path: `[${offerIndex}].transshipment`, value })}
                                                sx={{ textAlign: 'left' }}
                                                cellWidth={cellWidth}
                                                org={org}
                                            />
                                            {_.flatten(offer.sections.map((section, sectionIndex) => {
                                                return section.offers[0].fields.map((field, fieldIndex) => {
                                                    if (!product) return <TableCell key={field.id} />;

                                                    const fieldType = FieldTypes[transportationMethod].find(f => f.value === field.type);
                                                    const chargeLine = fieldType?.getChargeLines ? fieldType.getChargeLines({ field, products: [product], organization: org }) : null;
                                                    const fieldValueId = chargeLine ? chargeLine[0].fieldValueId : field.type;
                                                    const _multiplierText = chargeLine ? chargeLine[0].multiplierText : multiplierText;

                                                    let cleanValue = field.values[fieldValueId]?.value || '';
                                                    let currency = field.values[fieldValueId]?.currency || null;
                                                    const formula = field.values[fieldValueId]?.formula || null;

                                                    let value = '';
                                                    if (field.type === 'date') {
                                                        value = field.values.date?.value || '';
                                                    } else if (field.type === 'string') {
                                                        value = field.values.string.value || '';
                                                    } else {
                                                        cleanValue = cleanValue || 0;
                                                        currency = currency || org.defaults?.currency;
                                                        if (_multiplierText === 'ton') {
                                                            value = `${currencyToText(currency, true, cleanValue * 1000)} per ${pluralize(_multiplierText, 1)}`;
                                                        } else {
                                                            value = `${currencyToText(currency, true, cleanValue)} per ${pluralize(_multiplierText, 1)}`;
                                                        }
                                                    }

                                                    return (
                                                        <Cell
                                                            key={field.id}
                                                            type={field.type === 'date' ? 'date' : field.type === 'string' ? 'string' : currency ? 'currency' : 'string'}
                                                            value={value}
                                                            hidden={field.hidden}
                                                            cleanValue={currency ? { currency, value: cleanValue, formula  } : cleanValue}
                                                            field={field}
                                                            path={`${offer.id}:${field.id}`}
                                                            editable={editable}
                                                            multiplierText={multiplierText}
                                                            selected={selected}
                                                            highlighted={highlighted}
                                                            setEditable={setEditable}
                                                            setSelected={setSelected}
                                                            setValue={value => {
                                                                if (fieldValueId === 'string' || fieldValueId === 'date') {
                                                                    setField({ path: `[${offerIndex}].sections[${sectionIndex}].offers[0].fields[${fieldIndex}].values[${fieldValueId}].value`, value });
                                                                } else {
                                                                    setField({ path: `[${offerIndex}].sections[${sectionIndex}].offers[0].fields[${fieldIndex}].values[${fieldValueId}]`, value });
                                                                }
                                                            }}
                                                            sections={offers[offerIndex].sections}
                                                            products={[product]}
                                                            onAddBuyingRate={onAddBuyingRate}
                                                            onRemoveBuyingRate={onRemoveBuyingRate}
                                                            suppliers={suppliers}
                                                            offers={offers}
                                                            setOffers={setOffers}
                                                            setBuyingRates={setBuyingRates}
                                                            fieldPath={`[${offerIndex}].sections[${sectionIndex}].offers[0].fields[${fieldIndex}]`}
                                                            buyingRates={buyingRates}
                                                            offerId={offer.id}
                                                            onShowRates={onShowRates}
                                                            cellWidth={cellWidth}
                                                            exchangeRates={exchangeRates}
                                                            transportationMethod={transportationMethod}
                                                            disabled={mode !== 'rates' && mode !== 'tariff' && mode !== 'opportunities'}
                                                            org={org}
                                                        />
                                                    )
                                                });
                                            }))}
                                        </TableRow>
                                    );
                                })}
                                {!locked && mode === 'opportunities' && (
                                    <TableRow>
                                        <TableCell colSpan={5} sx={{ position: 'sticky', left: '0px', padding: '8px 12px', background: '#ffffff' }}>
                                            <Button 
                                                variant='text' 
                                                color='primary'
                                                onClick={() => setAddRows({ open: true, groupIndex })}
                                            >
                                                + Route
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                )}
                                {!locked && (mode === 'rates' || mode === 'tariff') && (
                                    <TableRow>
                                        <TableCell colSpan={5} sx={{ position: 'sticky', left: '0px', padding: '8px 12px', background: '#ffffff' }}>
                                            <Button 
                                                variant='text' 
                                                color='primary'
                                                onClick={() => onAddRate({ groupIndex })}
                                            >
                                                + {mode.startsWith('rates') ? 'Rate' : 'Tariff'}
                                            </Button>
                                            {mode === 'rates' && (
                                                <Button 
                                                    variant='text' 
                                                    color='primary'
                                                    onClick={() => onExportGroup({ groupIndex })}
                                                >
                                                    <FontAwesomeIcon icon={faFileExport} />
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            </Paper>
        );
    });
}

function Cell(props) {
    const { org, type, value, hidden, setValue, cleanValue, editable, selected, highlighted, path, setEditable, multiplierText,
        setSelected, disabled, disabledDelete, field, section, offerId, colSpan, onTitleAction, sx, sections, products,
        onAddBuyingRate, buyingRates, onShowRates, onRemoveBuyingRate, cellWidth, exchangeRates, transportationMethod,
        suppliers, offers, setOffers, setBuyingRates, fieldPath } = props;
    const [ local, setLocal ] = useState(null);
    const [ dropOver, setDropOver ] = useState(null);
    const [ showX, setShowX ] = useState(null);
    const [ titleAnchorEl, setTitleAnchorEl] = useState(null);
    const [ openFieldSettingsDialog, setOpenFieldSettingsDialog ] = useState(false);
    const theme = useTheme();

    const first = useRef();
    const setDropOverRef = useRef();
    const onAddBuyingRateRef = useRef();
    const productsRef = useRef();
    const offerIdRef = useRef();
    const fieldRef = useRef();
    setDropOverRef.current = setDropOver;
    onAddBuyingRateRef.current = onAddBuyingRate;
    productsRef.current = products;
    offerIdRef.current = offerId;
    fieldRef.current = field;

    const buyingRateSuppliers = !buyingRates ? [] : _((field || {}).buyingRates || []).map(fb => buyingRates.find(rates => rates.id === fb.rateId)).map(r => r.supplier.organization).uniq().value();

    const rates = field && buyingRates ? _.flatten(products.map(p => getFieldRatesForProduct({ fieldBuyingRates: (field || {}).buyingRates || [], product: p, buyingRates: buyingRates || [], transportationMethod, allProducts: products, organization: org }))) : null;
    const ratesText = rates ? ratesToText({ rates, transportationMethod, buyingRates, exchangeRates, products, organization: org }) : null;
    const ratesTotal = rates ? getTotalRates({ field, fieldBuyingRates: (field || {}).buyingRates || [], buyingRates, transportationMethod, products, exchangeRates, organization: org }) : null;

    const [ , drop ] = useDrop(() => ({ 
        accept: ['RateCardSingleRate', 'RateCardSingleSection', 'RateCard'],
        hover: (item, monitor) => {
            if (first.current) return;
            first.current = true;
            setDropOverRef.current(monitor.canDrop(item, monitor) ? 'candrop' : 'cantdrop');
            function isOver() {
                if (monitor.isOver()) {
                    setTimeout(isOver, 100);
                } else {
                    first.current = false;
                    setDropOverRef.current(null);
                }
            }
            setTimeout(isOver, 100);
        },
        drop: (item, monitor) => {
            onAddBuyingRateRef.current({ rate: item.rate, rateFieldId: item.field?.id, section: item.section, opportunityOfferId: offerIdRef.current, opportunityFieldId: fieldRef.current.id });
        },
        canDrop: (item, monitor) => {
            return _.some(productsRef.current, p => {
                const _type = item.rate.product.type;
                const _type2 = p.type;
                if (_type !== _type2) return false;
                if ((item.rate.product.dangerous || false) !== (p.dangerous || false)) return false;
                return true;
            });
        },
    }));

    const inputRef = useRef();
    const skipBlurRef = useRef();

    const kgToTon = field && ((field.type === 'per-unit' || field.type === 'per-unit-type') && multiplierText === 'ton');

    useEffect(() => {
        if (editable === path) {
            setLocal(cleanValue);
            if (inputRef.current) { 
                inputRef.current.focus(); 
                inputRef.current.select();
            }
        } else if (local) {
            if (setValue) {
                if (type === 'currency') {
                    setValue({...local, value: parseFloat(local.value) });
                } else {
                    setValue(local);
                }
            }
            setLocal(null);
        }
    }, [ editable, path ]);

    function onBlur(_path) {
        if (setValue) {
            if (type === 'currency') {
                setValue({...local, value: parseFloat(local.value) });
            } else {
                setValue(local);
            }
        }

        if (!skipBlurRef.current) {
            skipBlurRef.current = setTimeout(() => {
                skipBlurRef.current = false;
                setEditable(_editable => {
                    if (_editable === _path) {
                        // TODO, save fields
                        return null;
                    }
                    return _editable;
                });
            }, 200);
        }
    }

    function onCancelBlur() {
        if (_.isNumber(skipBlurRef.current)) {
            clearTimeout(skipBlurRef.current);
            skipBlurRef.current = false;
        } else {
            skipBlurRef.current = true;
            setTimeout(() => {
                skipBlurRef.current = false;
            }, 1000);
        }
    }

    const titleHandleClick = (event) => {
        setTitleAnchorEl(event.currentTarget);
    };
    const titleHandleClose = () => {
        setTitleAnchorEl(null);
    };

    let formulaStr = null;
    if ((type === 'currency') && (editable !== path) && (cleanValue.value || cleanValue.value === 0) && ((local || cleanValue).formula)) {
        formulaStr = formulaToReact({ 
            formula: (local || cleanValue).formula, 
            errorValue: 'Formula', 
            currency: (local || cleanValue).currency, 
            multiplierText, 
            sections, 
            fieldType: field?.type, 
            cost: ratesTotal * exchangeRates[(local || cleanValue).currency],
            width: '100%',
            buyingRatesCalculation: {
                buyingRates,
                products,
                exchangeRates,
                organization: org,
                transportationMethod,
                field,
                exchangeRates,
            }
        });
    }

    return (
        <>
        <TableCell
            colSpan={colSpan || 1}
            sx={{ 
                border: selected === path ? `2px solid ${blue[500]}` : `1px solid ${theme.palette.border1}`, 
                backgroundColor: highlighted?.includes(path) && selected !== path ? blue[50] : (sx?.background || 'inherit'),
                padding: type === 'currency' || editable === path ? '0px' : '8px 12px', 
                width: cellWidth,
                minWidth: cellWidth, 
                maxWidth: type === 'currency' ? '' : cellWidth, 
                textAlign: sx?.textAlign || (type === 'fieldtitle' || type === 'sectiontitle' ? 'center' : 'left'), 
                height: 'auto', 
                minHeight: '36px', 
                maxHeight: 'none', 
                position: 'relative',
                ...(type === 'route' ? {} : {
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                }),
                overflow: 'hidden',
                fontSize: 'inherit',
                ...(sx || {})
            }}
            onClick={() => {
                if (disabled) return;
                if (selected !== path) {
                    setEditable(null);
                    setSelected(path);
                } else {
                    setEditable(path);
                }
            }}
            onDoubleClick={() => {
                if (disabled) return;
                if (selected !== path) {
                    setSelected(path);
                    setEditable(path);
                }
            }}
            className='nonotch'
            ref={products && onAddBuyingRate ? drop : null}
        >
            {dropOver === 'candrop' && (
                <Box sx={{ position: 'absolute', background: grey[800], opacity: 0.8, top: '0px', left: '0px', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', textAlign: 'center', whiteSpace: 'normal', fontSize: '11px' }}>
                    Drop your rate here to add it to the buying rates.
                </Box>
            )}
            {dropOver === 'cantdrop' && (
                <Box sx={{ position: 'absolute', background: red[500], opacity: 0.8, top: '0px', left: '0px', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', textAlign: 'center', whiteSpace: 'normal', fontSize: '11px' }}>
                    Rate is not for a product included in the opportunity.
                </Box>
            )}
            {(type === 'fieldtitle' || type === 'sectiontitle') && editable === path && (
                <TitleCell
                    type={type}
                    field={field || section}
                    local={local}
                    hidden={hidden}
                    disabled={disabled}
                    disabledDelete={disabledDelete}
                    setLocal={setLocal}
                    onBlur={() => onBlur(path)}
                    onTitleAction={onTitleAction}
                    transportationMethod={transportationMethod}
                    organization={org}
                />
            )}
            {type !== 'currency' && type !== 'date' && editable !== path ? value : null}
            {type === 'date' && editable !== path && value ? dayjs(value).format('L') : null}
            {type !== 'currency' && type !== 'fieldtitle' && type !== 'sectiontitle' && (
                <TextField
                    type={type}
                    value={local}
                    onChange={e => setLocal(e.target.value)}
                    disabled={disabled}
                    inputRef={inputRef}
                    sx={{ display: editable === path ? 'block' : 'none' }}
                    inputProps={{ style: type === 'date' ? { padding: '3px', width: '90px', fontSize: '10px' } : {} }}
                    onKeyDown={e => {
                        if (e.keyCode === 13) {
                            if (onBlur) {
                                onBlur(path);
                            }
                            e.preventDefault();
                            e.stopPropagation();
                        }
                        if (e.keyCode === 27) {
                            setEditable(null);
                            e.preventDefault();
                        }
                    }}
                />
            )}
            {type === 'currency' && editable !== path && (cleanValue.value || cleanValue.value === 0) && (
                <Box sx={{ padding: '8px 12px', width: cellWidth + 32 }}>
                    {(local || cleanValue).formula ? formulaStr : value } 
                </Box>
            )}

            {type === 'currency' && editable === path && (
                <CurrencyFormulaTextField
                    showLCLWM={products?.some(p => p.type === 'LCL' || p.type === 'By Volume or Weight (sea)')}
                    value={multiplyIfFloat(local ? local.value : cleanValue.value, kgToTon ? 1000 : 1)}
                    currency={local ? local.currency : cleanValue.currency}
                    formula={local ? local.formula : cleanValue.formula}
                    setValue={(v) => {
                        const vv = parseFloatOrString(v, kgToTon ? 1000 : 1);
                        if (!local) {
                            // Happens when event is fired during focus() before local had a chance to update
                            setLocal({ ...cleanValue, value: vv })
                        } else {
                            setLocal({ ...local, value: vv })
                        }
                    }}
                    setCurrency={(v) => {
                        setLocal({ ...local, currency: v })
                    }}
                    setFormula={(v) => {
                        // Erasing the formula sends null - there is no currency to read off it
                        // and nothing to spread. The editor's Erase button did exactly that and
                        // took the page down instead of clearing the cell.
                        if (v && v.currency) {
                            setLocal({ ...local, ...v })
                        } else {
                            setLocal({ ...local, formula: v || null })
                        }
                    }}
                    focusedId={path}
                    focused={editable} 
                    setFocused={setSelected} 
                    onBlur={onBlur}
                    onCancelBlur={onCancelBlur}
                    disabled={disabled}
                    width={cellWidth}
                    inputRef={inputRef}
                    sections={sections}
                    organization={org}
                    withBuyingMargin={true}
                />
            )}
            {hidden && type === 'fieldtitle' && (
                <Box sx={{ 
                    position: 'absolute',
                    top: '0px',
                    left: editable === path ? '30px' : '0px', 
                    width: editable === path ? 'calc(100% - 30px)' : '100%', 
                    height: '100%', 
                    background: 'rgba(255,255,255,0.7)',
                    display: 'flex',
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    fontSize: '36px',
                    color: grey[500],
                }}>
                    <FontAwesomeIcon icon={faEyeSlash} />
                </Box>
            )}
            {hidden && type !== 'fieldtitle' && type !== 'sectiontitle' && (
                <Box sx={{ 
                    position: 'absolute',
                    top: '0px',
                    left: '0px', 
                    width: '100%', 
                    height: '100%', 
                    background: 'rgba(255,255,255,0.9)',
                    display: 'flex',
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    fontSize: '36px',
                    pointerEvents: 'none',
                }} />
            )}
            {!_.isEmpty(ratesText) && (
                <Alert severity='info' sx={{ fontSize: '11px' }} icon={false}>
                    <AlertTitle sx={{ fontSize: '11px' }}>Cost Rates</AlertTitle>
                    <span dangerouslySetInnerHTML={{ __html: ratesText.join('') }} />
                    <Box sx={{ cursor: 'pointer', textDecoration: 'underline', marginTop: '12px'}} onClick={() => {
                        setOpenFieldSettingsDialog(true);
                    }}>manually add another rate</Box>
                </Alert>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {buyingRateSuppliers.map(supplier => (
                    <Box sx={{ position: 'relative' }} key={supplier} onMouseEnter={() => setShowX(supplier)} onMouseLeave={() => setShowX(null)}>
                        <IconButton 
                            onClick={() => onShowRates({ opportunityOfferId: offerId, opportunityFieldId: field.id, supplier })}
                        >
                            <SupplierLogos size={SupplierLogos.size.MINI} organization={supplier} />
                        </IconButton>
                        <IconButton
                            color='error'
                            sx={{ position: 'absolute', right: '-10px', top: '-5px', display: showX === supplier ? 'block' : 'none' }}
                            onClick={e => {
                                e.stopPropagation();
                                e.preventDefault();
                                onRemoveBuyingRate({ supplier, opportunityOfferId: offerId, opportunityFieldId: field.id });
                            }}
                        >
                            <FontAwesomeIcon icon={faCircleXmark} />
                        </IconButton>
                    </Box>
                ))}
            </Box>
            {!_.isEmpty(ratesText) && editable !== path && (
                <ProfitWidget
                    sx={{ position: 'absolute', right: '0px', top: '0px', padding: '6px', fontSize: '9px' }}
                    mode='icon-with-hover'
                    text='Field'
                    section={{ displayCurrency: field.currency, displayCurrencySource: field.currencySource }}
                    sectionOffer={{ fields: [field] }}
                    sections={sections}
                    products={products}
                    buyingRates={buyingRates}
                    transportationMethod={transportationMethod}
                    exchangeRates={exchangeRates}
                    organization={org}
                />
            )}
        </TableCell>
        {openFieldSettingsDialog && (
            <FieldSettingsDialog
                open={openFieldSettingsDialog}
                initialCategory="buying"
                hideSidePanel
                offers={offers}
                onClose={() => setOpenFieldSettingsDialog(false)}
                field={field}
                buyingRates={buyingRates}
                onDone={({ field: updatedField, buyingRates: updatedBuyingRates }) => {
                    if (setOffers && fieldPath) {
                        setOffers(_offers => {
                            const newOffers = _.cloneDeep(_offers);
                            _.set(newOffers, fieldPath, updatedField);
                            return newOffers;
                        });
                    }
                    if (setBuyingRates) setBuyingRates(updatedBuyingRates);
                    setOpenFieldSettingsDialog(false);
                }}
                products={products}
                sections={sections}
                transportationMethod={transportationMethod}
                organization={org}
                suppliers={suppliers}
                exchangeRates={exchangeRates}
            />
        )}
        </>
    )
}

function TitleCell(props) {
    const { type, field, local, disabled, disabledDelete, hidden, onBlur, setLocal, onTitleAction, transportationMethod, organization } = props;
    const [ titleAnchorEl, setTitleAnchorEl] = useState(null);

    const inputRef = useRef();

    const titleHandleClick = (event) => {
        setTitleAnchorEl(event.currentTarget);
    };
    const titleHandleClose = () => {
        setTitleAnchorEl(null);
    };

    function onDateCell() {
        setTitleAnchorEl(null);
        onTitleAction('datecell');
    }

    function onHide() {
        setTitleAnchorEl(null);
        onTitleAction('fieldhide');
    }

    function onAddField() {
        setTitleAnchorEl(null);
        onTitleAction('addfield');
    }

    function onTrash() {
        setTitleAnchorEl(null);
        onTitleAction('deletefield');
    }

    function onReorder() {
        setTitleAnchorEl(null);
        onTitleAction('reorder');
    }

    function onChangeCurrency() {
        setTitleAnchorEl(null);
        onTitleAction('changecurrency');
    }

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', minHeight: '100%' }}>
            <Button
                variant='contained'
                color='primary'
                sx={{ width: '30px', minWidth: '30px', borderRadius: '0px', boxShadow: 'none', height: '100%' }}
                onClick={titleHandleClick}
            >
                <FontAwesomeIcon icon={faEllipsis} />
            </Button>
            <TextField
                sx={{ width: 'calc(100% - 30px)' }}
                value={local}
                onChange={e => {
                    setLocal(e.target.value)
                }}
                disabled={disabled}
                inputRef={inputRef}
                inputProps={{ style: { fontSize: '14px' } }}
                onKeyDown={e => {
                    if (e.keyCode === 13) {
                        if (onBlur) {
                            onBlur();
                        }
                        e.preventDefault();
                        e.stopPropagation();
                    }
                }}
            />
            <Menu
                anchorEl={titleAnchorEl}
                open={Boolean(titleAnchorEl)}
                onClose={titleHandleClose}
            >
                {type === 'fieldtitle' && (
                    FieldTypes[transportationMethod].map(f => 
                        f.isAggregated ? null : 
                        f.value === 'custom' ? null : 
                        (_.isFunction(f.enabled) && !f.enabled({ organization })) ? null :
                        (
                        <MenuItem key={f.value} onClick={() => {
                            setTitleAnchorEl(null);
                            onTitleAction(f.value);
                        }}>
                            {field.type === f.value && (
                                <ListItemIcon>
                                    <FontAwesomeIcon icon={faCheck} />
                                </ListItemIcon>
                            )}
                            <ListItemText inset={field.type !== f.value}>{f.label}</ListItemText>
                        </MenuItem>
                    ))
                )}
                {type === 'fieldtitle' && (
                    <MenuItem onClick={onDateCell}>
                        {field.type === 'date' && (
                            <ListItemIcon>
                                <FontAwesomeIcon icon={faCheck} />
                            </ListItemIcon>
                        )}
                        <ListItemText inset={field.type !== 'date'}>Date</ListItemText>
                    </MenuItem>
                )}
                {type === 'fieldtitle' && (
                    <Divider />
                )}
                {type === 'fieldtitle' && (
                    <MenuItem onClick={onChangeCurrency}>
                        <ListItemIcon>
                            <FontAwesomeIcon icon={faDollarSign} />
                        </ListItemIcon>
                        <ListItemText>Change currency</ListItemText>
                    </MenuItem>
                )}
                {type === 'fieldtitle' && (
                    <Divider />
                )}
                {type === 'fieldtitle' && (
                    <MenuItem onClick={onHide}>
                        <ListItemIcon>
                            <FontAwesomeIcon icon={faEye} />
                        </ListItemIcon>
                        <ListItemText>{ hidden ? 'Show column' : 'Hide column' }</ListItemText>
                    </MenuItem>
                )}
                {type === 'fieldtitle' && (
                    <Divider />
                )}
                {type !== 'fieldtitle' && (
                    <MenuItem onClick={onReorder}>
                        <ListItemIcon>
                            <FontAwesomeIcon icon={faShuffle} />
                        </ListItemIcon>
                        <ListItemText>Reorder fields</ListItemText>
                    </MenuItem>
                )}
                <MenuItem onClick={onAddField}>
                    <ListItemIcon>
                        <FontAwesomeIcon icon={faPlus} />
                    </ListItemIcon>
                    <ListItemText>{type === 'fieldtitle' ? 'Add field to the right' : 'Add section to the right'}</ListItemText>
                </MenuItem>
                <MenuItem sx={{ color: red[500] }} onClick={onTrash} disabled={disabledDelete}>
                    <ListItemIcon>
                        <FontAwesomeIcon style={{ color: red[500] }} icon={faTrash} />
                    </ListItemIcon>
                    <ListItemText>{type === 'fieldtitle' ? 'Delete this field' : 'Delete this section'}</ListItemText>
                </MenuItem>
            </Menu>
        </Box>
    );
}

function parseFloatOrString(s, divider = 1) {
    if (s.match(/^-?[0-9]+\.0+$/)) {
        if (divider === 1) return s;
        return parseFloat(s) / divider;
    }
    if (s.match(/^-?[0-9]+\.[0-9]+$/)) return parseFloat(s) / divider;
    if (s.match(/^-?[0-9]+$/)) return parseFloat(s) / divider;
    return s;
}

function multiplyIfFloat(v, mult) {
    if (_.isNumber(v)) return v * mult;
    return v;
}

function ValiditySpecialInputField(props) {
    const { icon, emptyStateText, disabled, value, onOpenModal } = props;

    const buttonText = (() => {
        if (!value.validUntil && !value.validFrom) {
            return emptyStateText;
        }

        if (value.validUntil && value.validFrom) {
            return `${dayjs(value.validFrom).format('L')} - ${dayjs(value.validUntil).format('L')}`;
        }

        if (value.validUntil && !value.validFrom) {
            return `Until ${dayjs(value.validUntil).format('L')}`;
        }

        if (!value.validUntil && value.validFrom) {
            return `From ${dayjs(value.validFrom).format('L')}`;
        }
    })();

    return (
        <Button
            variant='text'
            sx={{ textTransform: 'none', padding: emptyStateText === buttonText ? '0px' : '' }}
            color={buttonText === emptyStateText ? 'oneoffbuilderfields' : 'primary'}
            disabled={disabled}
            onClick={onOpenModal}
        >
            {icon}&nbsp;&nbsp;{buttonText}
        </Button>
    )
}

function findPathForField({ offers, offerId, fieldId }) {
    let offerIndex, sectionIndex, sectionOfferIndex, fieldIndex;

    offers.forEach((o, oi) => o.sections.forEach((s, si) => s.offers.forEach((so, soi) => so.fields.forEach((f, fi) => {
        if ((o.id === offerId) && (f.id === fieldId)) {
            offerIndex = oi;
            sectionIndex = si;
            sectionOfferIndex = soi;
            fieldIndex = fi;
        }
    }))));

    return {
        path: `[${offerIndex}].sections[${sectionIndex}].offers[${sectionOfferIndex}].fields[${fieldIndex}]`,
        offerIndex,
        sectionIndex,
        sectionOfferIndex,
        fieldIndex,
    }
}

function RatesDetailsWrapper(props) {
    const { organization, rates, initialEmphasize, onStartDragging, onEndDragging, minimizedSearch, onClose, onSave, exchangeRates, transportationMethod } = props;

    const [ emphasize, setEmphasize ] = useState(initialEmphasize);

    useEffect(() => setEmphasize(initialEmphasize), [initialEmphasize]);

    const changed = !!_.xorWith(emphasize, initialEmphasize, _.isEqual).length;

    return (
        <Box sx={{ width: minimizedSearch ? '637px' : '1157px', transition: 'width 0.2s', paddingTop: '45px', position: 'relative' }}>
            <Alert
                sx={{ position: 'fixed', top: '0px', width: minimizedSearch ? '637px' : '1157px', right: '0px', paddingRight: '60px', zIndex: 1000, backgroundColor: changed ? '' : '#fafbfc' }}
                severity='info'
                icon={changed ? null : false}
                action={changed ? null : (
                    <IconButton color='error' onClick={onClose}>
                        <FontAwesomeIcon style={{ fontSize: '14px' }} icon={faCircleXmark} />
                    </IconButton>
                )}
            >
                {changed ? <>
                    You've made changes. <b style={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={() => onSave(emphasize)}>Save</b> or <b style={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={() => setEmphasize(initialEmphasize)}>revert</b> them.
                </> : null}
            </Alert>
            <Box sx={{ width: '600px', padding: '10px 15px 24px 24px', height: '100vh', position: 'relative' }}>
                {(rates || []).map(r => (
                    <Box key={r.id} sx={{ paddingBottom: '24px' }}>
                        {r.source && (
                            <Box sx={{ marginBottom: '30px' }}>
                                <RateCard
                                    rate={r}
                                    exchangeRates={exchangeRates}
                                    transitDatesIndex={0}
                                    standalone={true}
                                    organization={organization}
                                />
                            </Box>
                        )}
                        <RatesDetails
                            organization={organization}
                            rates={[r]}
                            emphasize={emphasize}
                            onStartDragging={onStartDragging}
                            onEndDragging={onEndDragging}
                            setEmphasize={setEmphasize}
                            transportationMethod={transportationMethod}
                        />
                    </Box>
                ))}
            </Box>
        </Box>
    );
}
